import React, { useState, useEffect, useRef } from 'react';
import { 
  Sun, 
  Moon, 
  ArrowRight, 
  Shield, 
  Lock, 
  Cpu, 
  Menu, 
  X,
  ChevronDown,
  ChevronRight,
  Zap,
  Crop,
  Layers,
  RefreshCw,
  HelpCircle,
  BookOpen,
  Activity,
  Sparkles,
  Home,
  Check,
  ArrowUp,
  IdCard,
  Images,
  Maximize2,
  Camera,
  Grid3X3,
  Image as ImageIcon
} from 'lucide-react';
import { UserProfile } from './SignInModal';
import { supabase } from '../utils/supabaseClient';
import OfferPopup from './OfferPopup';
import Lenis from 'lenis';
import { motion } from 'motion/react';
import { cn } from '../utils/cn';

interface LandingPageProps {
  onLaunchTool: () => void;
  onPlanSelect: (planName: string) => void;
  onSignIn: (targetPath?: string, mode?: 'signin' | 'signup') => void;
  userProfile: UserProfile | null;
  onSignOut: () => void;
  currentView?: string;
  specialContent?: React.ReactNode;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  isTemp?: boolean;
  life?: number;
}

const landingFaqs = [
  {
    category: "General & Support",
    question: "What image formats can I use?",
    answer: "You can work with all popular formats including JPG, PNG, WebP, AVIF, and GIF. Convert between any of these easily."
  },
  {
    category: "General & Support",
    question: "Does it work on my phone?",
    answer: "Yes, SmartImagify is fully mobile-friendly. Use it on any phone, tablet, or computer with a web browser."
  },
  {
    category: "General & Support",
    question: "Do I need to install any software or extension?",
    answer: "No installation is required. SmartImagify is a progressive web app that runs directly in any modern web browser on desktop, tablet, or mobile."
  },
  {
    category: "General & Support",
    question: "How can I contact customer support?",
    answer: "You can reach our support team by clicking the Help icon in the dashboard or emailing us directly at support@smartimagify.com. We respond within 24 hours."
  },
  {
    category: "General & Support",
    question: "Can I use the tool offline?",
    answer: "Yes! Once loaded, the core image compression, resizing, and conversion engines work fully offline because all operations occur locally in your browser tab."
  },
  {
    category: "General & Support",
    question: "Is there a limit on how many images I can process?",
    answer: "Free guest users can process up to 10 images per day. Free registered accounts get 30 images, while Pro users enjoy unlimited local processing with no daily caps."
  },
  {
    category: "Compression & Engine",
    question: "Why is client-side compression faster?",
    answer: "We never send images to remote servers. All processing happens inside your browser using secure Canvas contexts, providing zero upload delay and offline capability."
  },
  {
    category: "Compression & Engine",
    question: "How does the AI compression work?",
    answer: "Our AI looks at your image and automatically picks the best settings to save the most space while keeping it looking great — saving up to 90% of the file size."
  },
  {
    category: "Compression & Engine",
    question: "What is the difference between Lossy and Lossless compression?",
    answer: "Lossy compression significantly reduces file size by discarding imperceptible image data, ideal for web use. Lossless compression keeps pixel-perfect original quality while reducing size slightly."
  },
  {
    category: "Compression & Engine",
    question: "Does compressing an image reduce its dimensions?",
    answer: "No, simple compression only optimizes the file size without changing the width or height. If you want to change dimensions, you can use our built-in Resizer or Cropper utilities."
  },
  {
    category: "Compression & Engine",
    question: "Can I batch process multiple images at once?",
    answer: "Yes! SmartImagify supports multi-file batch operations. You can drag and drop dozens of images to compress, convert, or resize them simultaneously in one batch."
  },
  {
    category: "Compression & Engine",
    question: "What is the background removal engine powered by?",
    answer: "Our AI background mask tool uses a lightweight deep-learning model compiled to WebAssembly that runs directly on your local CPU or GPU using WebGL acceleration."
  },
  {
    category: "Privacy & Security",
    question: "Are my files uploaded or saved to any external servers?",
    answer: "Absolutely not. All compression, vectorization, cropping, and background masking occur locally within your browser tab environment using WebAssembly. Your images never leave your local device."
  },
  {
    category: "Privacy & Security",
    question: "Are my photos kept private?",
    answer: "Yes, 100%! Your photos are processed entirely on your own device. Nothing is ever uploaded to any server."
  },
  {
    category: "Privacy & Security",
    question: "Do you collect or sell any personal data?",
    answer: "No. We do not collect, store, or sell any of your uploaded media files or personal data. We only store basic account preferences in our database to manage subscriptions."
  },
  {
    category: "Privacy & Security",
    question: "Is it safe to compress confidential business documents?",
    answer: "Yes, it is completely secure. Because the processing occurs client-side in the sandbox of your browser, no third-party (including our servers) can view or access your documents."
  },
  {
    category: "Privacy & Security",
    question: "Does the app require any dangerous device permissions?",
    answer: "No. The application only accesses files you explicitly choose to upload/drag-and-drop into the browser window, requiring no system-level or camera permissions."
  },
  {
    category: "Privacy & Security",
    question: "Do you use cookies or tracking pixels on this site?",
    answer: "We only use essential functional cookies to keep you signed into your account and secure your sessions. We do not load any invasive third-party ad-tracking or profile cookies."
  },
  {
    category: "Plans & Billing",
    question: "Is there a file size limit?",
    answer: "Free users can process files up to 5MB each. Pro members enjoy files up to 50MB with no limits on batch size."
  },
  {
    category: "Plans & Billing",
    question: "What is included in the Smart Imagify lifetime plan?",
    answer: "The lifetime plan offers access to all offline compression, conversion, and resizing engines without recurring fees, alongside priority customer support."
  },
  {
    category: "Plans & Billing",
    question: "Can I cancel my monthly/yearly subscription at any time?",
    answer: "Absolutely. You can cancel your subscription with a single click from your billing dashboard. You will retain Pro access until the end of your current billing period."
  },
  {
    category: "Plans & Billing",
    question: "Do you offer a money-back guarantee?",
    answer: "Yes! We offer a 14-day refund window if you are not fully satisfied with our premium plans. Just contact support and we will process it immediately."
  },
  {
    category: "Plans & Billing",
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards, debit cards, Apple Pay, Google Pay, and Link, processed securely through Stripe."
  },
  {
    category: "Plans & Billing",
    question: "Is there a free trial for the Pro plan?",
    answer: "We do not offer a timed trial, but our Free Tier is highly generous and lets you try all core features with smaller file sizes before upgrading."
  }
];

export default function LandingPage({
  onLaunchTool,
  onPlanSelect,
  onSignIn,
  userProfile,
  onSignOut,
  currentView,
  specialContent,
  theme,
  toggleTheme
}: LandingPageProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<'tools' | 'pricing' | 'resources' | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [activeReward, setActiveReward] = useState<{ type: 'credit' | 'discount' } | null>(null);
  const [smashedCredits, setSmashedCredits] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('si_smashed_credits');
      return stored ? Math.min(parseInt(stored, 10) || 0, 100) : 0;
    } catch {
      return 0;
    }
  });
  const [isCreditsClaimed, setIsCreditsClaimed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('si_bubble_credits_claimed') === 'true';
    } catch {
      return false;
    }
  });

  // Top announcement bar states
  const [topBarConfig, setTopBarConfig] = useState<any>(null);
  const [isTopBarOpen, setIsTopBarOpen] = useState(() => {
    try {
      const dismissedTimeStr = localStorage.getItem('si_topbar_dismissed_time');
      if (dismissedTimeStr) {
        const dismissedTime = parseInt(dismissedTimeStr, 10);
        if (!isNaN(dismissedTime) && Date.now() - dismissedTime < 24 * 60 * 60 * 1000) {
          return false;
        }
      }
      return true;
    } catch {
      return true;
    }
  });
  const [currentOfferIndex, setCurrentOfferIndex] = useState(0);

  useEffect(() => {
    const fetchTopBar = async () => {
      try {
        const { data: config } = await supabase
          .from('landing_config')
          .select('offer_badge_text')
          .limit(1)
          .single();
        
        if (config && config.offer_badge_text && config.offer_badge_text.startsWith('{"enabled":')) {
          setTopBarConfig(JSON.parse(config.offer_badge_text));
          return;
        }
      } catch (e) {}

      const localTopBar = localStorage.getItem('si_admin_topbar_override');
      if (localTopBar) {
        try {
          setTopBarConfig(JSON.parse(localTopBar));
        } catch {}
      }
    };
    
    fetchTopBar();

    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'si_admin_topbar_override') {
        try {
          localStorage.removeItem('si_topbar_dismissed_time');
        } catch {}
        setIsTopBarOpen(true);
        fetchTopBar();
      }
    };
    window.addEventListener('storage', handleStorage);
    
    const handleCustomStorage = () => {
      try {
        localStorage.removeItem('si_topbar_dismissed_time');
      } catch {}
      setIsTopBarOpen(true);
      fetchTopBar();
    };
    window.addEventListener('si_admin_topbar_updated', handleCustomStorage);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('si_admin_topbar_updated', handleCustomStorage);
    };
  }, []);

  useEffect(() => {
    if (!isTopBarOpen || !topBarConfig || !topBarConfig.slides || topBarConfig.slides.length <= 1) return;
    const speed = (topBarConfig.rotationSpeed ?? 5) * 1000;
    const interval = setInterval(() => {
      setCurrentOfferIndex(prev => (prev + 1) % topBarConfig.slides.length);
    }, speed);
    return () => clearInterval(interval);
  }, [isTopBarOpen, topBarConfig]);

  useEffect(() => {
    document.documentElement.classList.add('landing-page-active');
    
    // Check if it's a mobile/tablet device or touch screen or smaller screen size
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                     (window.innerWidth < 1024) || 
                     (navigator.maxTouchPoints > 0);

    let lenis: Lenis | null = null;
    let rafId: number;

    if (!isMobile) {
      // Force scroll-behavior auto on html element to prevent any jitter with Lenis
      document.documentElement.style.scrollBehavior = 'auto';
      
      lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });

      const raf = (time: number) => {
        lenis?.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    } else {
      // For mobile/touch devices, ensure native smooth scrolling is allowed
      document.documentElement.style.scrollBehavior = 'smooth';
    }

    return () => {
      document.documentElement.classList.remove('landing-page-active');
      document.documentElement.style.scrollBehavior = '';
      if (lenis) {
        lenis.destroy();
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    const checkClaimedStatus = () => {
      try {
        setIsCreditsClaimed(localStorage.getItem('si_bubble_credits_claimed') === 'true');
      } catch {}
    };
    checkClaimedStatus();

    window.addEventListener('si_bubble_credits_fully_claimed', checkClaimedStatus);
    window.addEventListener('storage', checkClaimedStatus);
    return () => {
      window.removeEventListener('si_bubble_credits_fully_claimed', checkClaimedStatus);
      window.removeEventListener('storage', checkClaimedStatus);
    };
  }, [userProfile]);

  useEffect(() => {
    if (window.location.search.includes('reset')) {
      try {
        localStorage.removeItem('si_smashed_credits');
        localStorage.removeItem('si_bubble_credits_claimed');
        localStorage.removeItem('si_bubble_credits_amount');
      } catch {}
      setSmashedCredits(0);
      setIsCreditsClaimed(false);
      try {
        const newurl = window.location.protocol + "//" + window.location.host + window.location.pathname;
        window.history.pushState({ path: newurl }, '', newurl);
      } catch {}
    }
  }, []);

  useEffect(() => {
    const handleBubbleSmashed = () => {
      setSmashedCredits(prev => {
        const next = Math.min(prev + 10, 100);
        try {
          localStorage.setItem('si_smashed_credits', next.toString());
          localStorage.setItem('si_bubble_credits_amount', next.toString());
        } catch {}
        return next;
      });
    };
    window.addEventListener('si_bubble_smashed', handleBubbleSmashed);
    return () => window.removeEventListener('si_bubble_smashed', handleBubbleSmashed);
  }, []);

  const handleMouseEnter = (menu: 'tools' | 'pricing' | 'resources') => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleSliderMove = (e: any) => {
    const container = document.getElementById('image-slider-container');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPosition((x / rect.width) * 100);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [hoveredMockup, setHoveredMockup] = useState<'left' | 'right' | 'center' | null>(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [activeLandingCat, setActiveLandingCat] = useState('General & Support');
  const [activeToolIdx, setActiveToolIdx] = useState(8);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [containerLeft, setContainerLeft] = useState(0);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);
  const [isManualAnimating, setIsManualAnimating] = useState(false);

  const [transitionDuration, setTransitionDuration] = useState(1000);
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Monitor container width and left offset dynamically via ResizeObserver for layout precision
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setWindowWidth(rect.width);
        setContainerLeft(rect.left);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay snapping slider transition loop
  useEffect(() => {
    if (isAutoplayPaused) return;
    const interval = setInterval(() => {
      setTransitionDuration(1000);
      setActiveToolIdx(prev => prev + 1);
    }, 4500);
    return () => clearInterval(interval);
  }, [isAutoplayPaused]);

  // Reset transition duration back to 1000ms after a wrap reset to 0ms
  useEffect(() => {
    if (transitionDuration === 0) {
      if (trackRef.current) {
        const _ = trackRef.current.offsetHeight; // Flush layout
      }
      setTransitionDuration(1000);
    }
  }, [transitionDuration]);

  const triggerManualSlide = (targetIdx: number) => {
    setTransitionDuration(1000);
    setActiveToolIdx(targetIdx);
  };

  const handleTransitionEnd = () => {
    if (activeToolIdx >= 16) {
      setTransitionDuration(0);
      setActiveToolIdx(activeToolIdx - 8);
    } else if (activeToolIdx < 8) {
      setTransitionDuration(0);
      setActiveToolIdx(activeToolIdx + 8);
    }
  };
  const [dbPackages, setDbPackages] = useState<any[]>([]);
  const [dbFaqs, setDbFaqs] = useState<any[]>([]);

  // Premium Footer States
  const [footerEmail, setFooterEmail] = useState('');
  const [footerSubscribed, setFooterSubscribed] = useState(() => {
    try {
      return localStorage.getItem('si_footer_newsletter_subscribed') === 'true';
    } catch {
      return false;
    }
  });
  const [footerSubmitting, setFooterSubmitting] = useState(false);
  const [footerLanguage, setFooterLanguage] = useState('en');
  const [footerCurrency, setFooterCurrency] = useState('USD');
  const [imageCounter, setImageCounter] = useState(1428590);

  // Custom Dropdown States & Refs
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [currDropdownOpen, setCurrDropdownOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const currRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', label: '🇺🇸 EN' },
    { code: 'es', label: '🇪🇸 ES' },
    { code: 'de', label: '🇩🇪 DE' },
    { code: 'fr', label: '🇫🇷 FR' },
    { code: 'ur', label: '🇵🇰 UR' }
  ];

  const currencies = [
    { code: 'USD', label: 'USD ($)' },
    { code: 'EUR', label: 'EUR (€)' },
    { code: 'GBP', label: 'GBP (£)' },
    { code: 'PKR', label: 'PKR (Rs)' }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
      if (currRef.current && !currRef.current.contains(event.target as Node)) {
        setCurrDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setImageCounter(prev => prev + Math.floor(Math.random() * 3) + 1);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const handleFooterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!footerEmail || !footerEmail.includes('@')) return;
    
    setFooterSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    try {
      localStorage.setItem('si_footer_newsletter_subscribed', 'true');
      localStorage.setItem('si_footer_newsletter_email', footerEmail);
    } catch {}
    setFooterSubmitting(false);
    setFooterSubscribed(true);
  };
  const [heroSlides, setHeroSlides] = useState<any[]>([
    {
      id: 'slide-4',
      hero_title: 'Professional Passport & ID Photo Studio',
      hero_subtitle: 'Generate standard biometric passport photos for any country. Automate background removal, sizing guidelines, and print sheet layouts.',
      hero_cta_text: 'Create Passport Photo',
      hero_cta_link: '/dashboard',
      show_offer_badge: true,
      offer_badge_text: '📸 Biometric Compliant'
    },
    {
      id: 'slide-1',
      hero_title: 'All-in-One Offline Image Studio',
      hero_subtitle: 'Compress, crop, resize and optimize your photos locally with supercharged client-side processing. Your files never leave your computer.',
      hero_cta_text: 'Open Studio',
      hero_cta_link: '/dashboard',
      show_offer_badge: true,
      offer_badge_text: '⚡ 100% Offline and Private'
    },
    {
      id: 'slide-2',
      hero_title: 'Smart AI-Powered Object Eraser',
      hero_subtitle: 'Instantly remove watermarks, unwanted objects, and timestamps from any photo inside your browser using fast offline neural execution.',
      hero_cta_text: 'Erase Objects',
      hero_cta_link: '/dashboard',
      show_offer_badge: true,
      offer_badge_text: '🎉 Smart Photo Cleaner'
    },
    {
      id: 'slide-3',
      hero_title: 'Batch Processing and Studio Suite',
      hero_subtitle: 'Convert, optimize, and edit hundreds of images in bulk with ultra-fast parallel local execution. No server limits or queues.',
      hero_cta_text: 'Open Studio',
      hero_cta_link: '/dashboard',
      show_offer_badge: true,
      offer_badge_text: '✨ Bulk Studio Toolkit'
    }
  ]);
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  const activeSlide = heroSlides[currentSlideIdx];
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);
  const isCanvasVisibleRef = useRef(true);
  const mouseRef = useRef({ x: 0, y: 0, active: false });

  // Fetch landing configuration and slides
  useEffect(() => {
    const fetchLandingConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('landing_config')
          .select('hero_slides, hero_title, hero_subtitle, hero_cta_text, hero_cta_link, show_offer_badge, offer_badge_text')
          .limit(1)
          .single();
        
        if (!error && data) {
          let slides = data.hero_slides;
          
          // Local storage override support (just like LandingConfigEditor does)
          try {
            const localStr = localStorage.getItem('si_admin_hero_override');
            if (localStr) {
              const parsed = JSON.parse(localStr);
              if (parsed.hero_slides) {
                slides = parsed.hero_slides;
              }
            }
          } catch {}

          if (slides && slides.length > 0) {
            setHeroSlides(slides);
          } else {
            // Fallback to 3 premium default slides, merging legacy single slide custom columns from db into the first slide
            setHeroSlides([
              {
                id: 'slide-4',
                hero_title: data.hero_title || 'Professional Passport & ID Photo Studio',
                hero_subtitle: data.hero_subtitle || 'Generate standard biometric passport photos for any country. Automate background removal, sizing guidelines, and print sheet layouts.',
                hero_cta_text: data.hero_cta_text || 'Create Passport Photo',
                hero_cta_link: data.hero_cta_link || '/dashboard',
                show_offer_badge: data.show_offer_badge !== undefined ? data.show_offer_badge : true,
                offer_badge_text: data.offer_badge_text || '📸 Biometric Compliant'
              },
              {
                id: 'slide-1',
                hero_title: 'All-in-One Offline Image Studio',
                hero_subtitle: 'Compress, crop, resize and optimize your photos locally with supercharged client-side processing. Your files never leave your computer.',
                hero_cta_text: 'Open Studio',
                hero_cta_link: '/dashboard',
                show_offer_badge: true,
                offer_badge_text: '⚡ 100% Offline and Private'
              },
              {
                id: 'slide-2',
                hero_title: 'Smart AI-Powered Object Eraser',
                hero_subtitle: 'Instantly remove watermarks, unwanted objects, and timestamps from any photo inside your browser using fast offline neural execution.',
                hero_cta_text: 'Erase Objects',
                hero_cta_link: '/dashboard',
                show_offer_badge: true,
                offer_badge_text: '🎉 Smart Photo Cleaner'
              },
              {
                id: 'slide-3',
                hero_title: 'Batch Processing and Studio Suite',
                hero_subtitle: 'Convert, optimize, and edit hundreds of images in bulk with ultra-fast parallel local execution. No server limits or queues.',
                hero_cta_text: 'Open Studio',
                hero_cta_link: '/dashboard',
                show_offer_badge: true,
                offer_badge_text: '✨ Bulk Studio Toolkit'
              }
            ]);
          }
        }
      } catch (err) {
        console.error('Error fetching landing config:', err);
      }
    };

    fetchLandingConfig();
    
    // Listen for storage changes to instantly update preview if edited in admin portal
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'si_admin_hero_override') {
        fetchLandingConfig();
      }
    };
    const handleCustomUpdate = () => {
      fetchLandingConfig();
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('si_admin_hero_updated', handleCustomUpdate);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('si_admin_hero_updated', handleCustomUpdate);
    };
  }, []);

  // Fetch packages and FAQs dynamically
  useEffect(() => {
    const fetchPackagesAndFaqs = async () => {
      try {
        const { data: pkgData } = await supabase
          .from('packages')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });
        if (pkgData && pkgData.length > 0) {
          setDbPackages(pkgData);
        }
      } catch (e) {
        console.error('Error fetching packages:', e);
      }

      try {
        const { data: faqData } = await supabase
          .from('faqs')
          .select('*')
          .eq('is_active', true)
          .eq('category', 'homepage')
          .order('sort_order', { ascending: true });
        if (faqData && faqData.length > 0) {
          setDbFaqs(faqData);
        }
      } catch (e) {
        console.error('Error fetching faqs:', e);
      }
    };
    fetchPackagesAndFaqs();
  }, []);

  // Slide rotation interval
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlideIdx(prev => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroSlides]);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      setShowBackToTop(window.scrollY > 400);
      isCanvasVisibleRef.current = window.scrollY < window.innerHeight + 100;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Static 3D Perspective Grid effect (drawn once on mount/resize/theme change instead of at 60fps)
  useEffect(() => {
    const canvas = gridCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const drawGrid = () => {
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);

      // Completely static vanishing point
      const currentXv = width / 2;
      const currentYv = height * 0.08; // Horizon starts at 8% of canvas height (pushed higher)

      // Define gradients for premium lines and glowing neon look (subtle and light)
      const lineGrad = ctx.createLinearGradient(0, currentYv, 0, height);
      const glowGrad = ctx.createLinearGradient(0, currentYv, 0, height);

      if (theme === 'light') {
        // Subtle grey grid in light mode
        lineGrad.addColorStop(0, 'rgba(100, 116, 139, 0.0)');
        lineGrad.addColorStop(0.38, 'rgba(100, 116, 139, 0.0)'); 
        lineGrad.addColorStop(0.55, 'rgba(100, 116, 139, 0.05)');
        lineGrad.addColorStop(0.75, 'rgba(100, 116, 139, 0.15)'); 
        lineGrad.addColorStop(0.9, 'rgba(100, 116, 139, 0.05)');
        lineGrad.addColorStop(1, 'rgba(100, 116, 139, 0.0)');

        glowGrad.addColorStop(0, 'rgba(100, 116, 139, 0.0)');
        glowGrad.addColorStop(0.38, 'rgba(100, 116, 139, 0.0)');
        glowGrad.addColorStop(0.55, 'rgba(100, 116, 139, 0.01)');
        glowGrad.addColorStop(0.75, 'rgba(100, 116, 139, 0.04)'); 
        glowGrad.addColorStop(0.9, 'rgba(100, 116, 139, 0.01)');
        glowGrad.addColorStop(1, 'rgba(100, 116, 139, 0.0)');
      } else {
        // High premium silver/grey grid in dark mode
        lineGrad.addColorStop(0, 'rgba(148, 163, 184, 0.0)');
        lineGrad.addColorStop(0.38, 'rgba(148, 163, 184, 0.0)'); 
        lineGrad.addColorStop(0.55, 'rgba(148, 163, 184, 0.08)');
        lineGrad.addColorStop(0.75, 'rgba(148, 163, 184, 0.22)'); 
        lineGrad.addColorStop(0.9, 'rgba(148, 163, 184, 0.08)');
        lineGrad.addColorStop(1, 'rgba(148, 163, 184, 0.0)');

        glowGrad.addColorStop(0, 'rgba(148, 163, 184, 0.0)');
        glowGrad.addColorStop(0.38, 'rgba(148, 163, 184, 0.0)');
        glowGrad.addColorStop(0.55, 'rgba(148, 163, 184, 0.02)');
        glowGrad.addColorStop(0.75, 'rgba(148, 163, 184, 0.08)'); 
        glowGrad.addColorStop(0.9, 'rgba(148, 163, 184, 0.02)');
        glowGrad.addColorStop(1, 'rgba(148, 163, 184, 0.0)');
      }

      // Draw Grid - Pass 1: Neon Ambient Glow
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = glowGrad;

      // Converging vertical lines
      const M = 40;
      for (let j = 0; j <= M; j++) {
        const fraction = j / M;
        const xBottom = width * (-0.8 + fraction * 2.6);
        ctx.beginPath();
        ctx.moveTo(currentXv, currentYv);
        ctx.lineTo(xBottom, height);
        ctx.stroke();
      }

      // Horizontal perspective lines
      const N = 26;
      for (let i = 0; i < N; i++) {
        const z = i / N;
        const lineY = currentYv + (height - currentYv) * Math.pow(z, 3.2);
        ctx.beginPath();
        ctx.moveTo(0, lineY);
        ctx.lineTo(width, lineY);
        ctx.stroke();
      }

      // Draw Grid - Pass 2: Core crisp lines
      ctx.lineWidth = 1.0;
      ctx.strokeStyle = lineGrad;

      // Converging vertical lines
      for (let j = 0; j <= M; j++) {
        const fraction = j / M;
        const xBottom = width * (-0.8 + fraction * 2.6);
        ctx.beginPath();
        ctx.moveTo(currentXv, currentYv);
        ctx.lineTo(xBottom, height);
        ctx.stroke();
      }

      // Horizontal perspective lines
      for (let i = 0; i < N; i++) {
        const z = i / N;
        const lineY = currentYv + (height - currentYv) * Math.pow(z, 3.2);
        ctx.beginPath();
        ctx.moveTo(0, lineY);
        ctx.lineTo(width, lineY);
        ctx.stroke();
      }
    };

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight * 0.60; // Expanded to 60% of viewport height
      drawGrid();
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [theme]);

  // Full screen interactive floating water bubbles canvas with rewards popups
  useEffect(() => {
    // Bubble system disabled to optimize scroll performance and load speed
    return () => {};
  }, [theme, userProfile, currentView]);

  const renderFormattedTitle = (title: string) => {
    if (!title) return null;
    
    // Support breaking lines with custom line breaks
    const parts = title.split(/\n|\\n|\|/);
    if (parts.length > 1) {
      return (
        <>
          <span className="font-extrabold">{parts[0]}</span> <br />
          <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>
            {parts.slice(1).join(' ')}
          </span>
        </>
      );
    }
    
    // Otherwise, highlight the last 1 or 2 words depending on length
    const words = title.split(' ');
    if (words.length > 2) {
      const mainPart = words.slice(0, words.length - 2).join(' ');
      const highlightPart = words.slice(words.length - 2).join(' ');
      return (
        <>
          <span className="font-extrabold">{mainPart}</span> <br />
          <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>
            {highlightPart}
          </span>
        </>
      );
    }
    return <span className="font-extrabold">{title}</span>;
  };

  const triggerRoute = (path: string) => {
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleNavClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    triggerRoute(path);
  };

  return (
    <div className={`min-h-screen text-[#f4f4f7] flex flex-col relative select-none overflow-x-hidden ${
      theme === 'light' 
        ? 'light bg-[#fafaff] text-[#090b11]' 
        : 'bg-[#040406] dark-mode-gradient-bg'
    }`}>
      
      {/* 0. GOOGLE FONTS & STYLING CLASSES */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        
        .premium-font-heading {
          font-family: 'Inter', sans-serif;
          letter-spacing: -0.03em;
        }
        .premium-font-body {
          font-family: 'Inter', sans-serif;
        }
        .fade-mask {
          mask-image: radial-gradient(circle 950px at 50% 50%, black 40%, transparent 98%);
          -webkit-mask-image: radial-gradient(circle 950px at 50% 50%, black 40%, transparent 98%);
        }
        @keyframes float-orb {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, 30px) scale(1.1); }
        }
        .animate-float-orb {
          animation: float-orb 16s ease-in-out infinite;
        }
        .dark-mode-gradient-bg {
          background-color: #040406;
          background-image: 
            radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.08) 0%, transparent 60%),
            radial-gradient(circle at 50% 100%, rgba(255, 255, 255, 0.02) 0%, transparent 60%);
        }
        @keyframes slide-push-in {
          0% { transform: translateY(12px); opacity: 0; filter: blur(6px); }
          100% { transform: translateY(0); opacity: 1; filter: blur(0); }
        }
        .animate-slide-push {
          animation: slide-push-in 0.7s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        @keyframes fade-slide-down {
          0% { transform: translateY(-8px) scaleY(0.98); opacity: 0; }
          100% { transform: translateY(0) scaleY(1); opacity: 1; }
        }
        .animate-fade-slide-down {
          animation: fade-slide-down 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes marquee-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          width: max-content;
          animation: marquee-scroll 25s linear infinite;
          will-change: transform;
        }
        .screenshots-fade-mask {
          mask-image: linear-gradient(to bottom, black 0%, black 35%, rgba(0, 0, 0, 0.75) 60%, transparent 98%);
          -webkit-mask-image: linear-gradient(to bottom, black 0%, black 35%, rgba(0, 0, 0, 0.75) 60%, transparent 98%);
        }
        .canvas-grid-bg-light {
          background-size: 70px 70px;
          background-image: linear-gradient(to right, rgba(0, 0, 0, 0.035) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(0, 0, 0, 0.035) 1px, transparent 1px);
        }
        .canvas-grid-bg-dark {
          background-size: 70px 70px;
          background-image: linear-gradient(to right, rgba(255, 255, 255, 0.025) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
        }
      `}</style>

      {/* FULL VIEWPORT NEURAL SYSTEM BACKGROUND GRID */}
      <div 
        className={`fixed inset-0 w-full h-full pointer-events-none z-0 fade-mask opacity-85 ${
          theme === 'light' ? 'canvas-grid-bg-light' : 'canvas-grid-bg-dark'
        }`} 
      />

      {/* INTERACTIVE PERSPECTIVE GRID CANVAS */}
      <canvas 
        ref={gridCanvasRef} 
        className="fixed bottom-0 left-0 w-full h-[60vh] pointer-events-none z-10" 
      />

      {/* Background glow orbs removed */}
      {activeDropdown && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-[2px] z-40 transition-all duration-300 pointer-events-none" />
      )}

      {/* HEADER & TOP ANNOUNCEMENT BAR WRAPPER */}
      <div className="fixed top-0 left-0 w-full z-50 flex flex-col">
        {/* GLOBAL TOP ANNOUNCEMENT BAR */}
        {isTopBarOpen && topBarConfig?.enabled && topBarConfig?.showOnLanding && topBarConfig?.slides?.length > 0 && (
          <div className={`landing-top-bar w-full min-h-[38px] border-b relative overflow-hidden flex items-center shrink-0 py-1.5 ${
            theme === 'light' 
              ? 'bg-white border-zinc-200' 
              : 'bg-black border-zinc-900'
          }`}>
            {/* Subtle grid overlay */}
            <div className={`absolute inset-0 bg-[size:16px_16px] opacity-100 pointer-events-none ${
              theme === 'light'
                ? 'bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)]'
                : 'bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)]'
            }`} />
            
            <style>{`
              @keyframes marquee {
                0% { transform: translateX(0%); }
                100% { transform: translateX(-50%); }
              }
              .animate-marquee {
                display: flex;
                width: max-content;
                animation: marquee 30s linear infinite;
              }
              .animate-marquee:hover {
                animation-play-state: paused;
              }
              @keyframes float-mockup-1 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-7px); }
              }
              @keyframes float-mockup-2 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-11px); }
              }
              @keyframes float-mockup-3 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-5px); }
              }
              .animate-float-1 {
                animation: float-mockup-1 8s ease-in-out infinite;
              }
              .animate-float-2 {
                animation: float-mockup-2 6.5s ease-in-out infinite;
              }
              .animate-float-3 {
                animation: float-mockup-3 9s ease-in-out infinite;
              }
            `}</style>

            {/* Left & Right gradient masks for smooth fade edges */}
            <div className={`absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r to-transparent pointer-events-none z-10 ${
              theme === 'light' ? 'from-white' : 'from-black'
            }`} />
            <div className={`absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l to-transparent pointer-events-none z-10 ${
              theme === 'light' ? 'from-white via-white/90' : 'from-black via-black/90'
            }`} />

            {/* Continuous scrolling marquee */}
            <div className="animate-marquee flex items-center gap-16 select-none whitespace-nowrap pl-4 pr-12">
              {[...topBarConfig.slides, ...topBarConfig.slides, ...topBarConfig.slides, ...topBarConfig.slides].map((slide: any, idx: number) => (
                <div 
                  key={`${slide.id}-${idx}`} 
                  className={`flex items-center gap-3 font-extrabold text-[11px] md:text-xs tracking-wider uppercase whitespace-nowrap ${
                    theme === 'light' ? 'text-zinc-700' : 'text-zinc-300'
                  }`}
                >
                  <span>{slide.text}</span>
                  {slide.buttonText && (
                    <a
                      href={slide.buttonLink || '#'}
                      onClick={(e) => {
                        if (slide.buttonLink?.startsWith('#')) {
                          e.preventDefault();
                          const targetId = slide.buttonLink.substring(1);
                          const el = document.getElementById(targetId);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth' });
                          }
                        } else if (slide.buttonLink === '/dashboard' || slide.buttonLink === '/studio') {
                          e.preventDefault();
                          onLaunchTool();
                        }
                      }}
                      className={`ml-1.5 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-full transition-all cursor-pointer ${
                        theme === 'light'
                          ? 'bg-zinc-900 hover:bg-zinc-700 text-white'
                          : 'bg-white hover:bg-zinc-200 text-black'
                      }`}
                    >
                      {slide.buttonText}
                    </a>
                  )}
                </div>
              ))}
            </div>

            {/* Close / Dismiss Button */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsTopBarOpen(false);
                try {
                  localStorage.setItem('si_topbar_dismissed_time', Date.now().toString());
                } catch {}
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors p-1 border border-[var(--border)] rounded-md cursor-pointer z-20 text-[var(--text-3)] hover:text-[var(--text-1)] bg-[var(--bg-2)]/60"
              title="Dismiss announcement"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* 1. FLOATING HEADER */}
        <header className={`fixed left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-7xl transition-all duration-500 rounded-xl border border-[var(--border)] bg-[var(--sidebar-bg)] shadow-[var(--shadow-premium)] pointer-events-auto ${
          (isTopBarOpen && topBarConfig?.enabled && topBarConfig?.showOnLanding && topBarConfig?.slides?.length > 0)
            ? 'top-14 md:top-16'
            : 'top-4 md:top-6'
        } ${mobileMenuOpen ? 'py-4 px-6' : scrolled ? 'py-2 px-6' : 'py-3 px-6'}`}>
          
          <div className="flex items-center justify-between gap-4">
            {/* Left: Logo & Brand (No "Sandbox Secured" badge per user request) */}
            <div className="flex items-center gap-3 shrink-0">
              <div 
                className="flex items-center gap-2 cursor-pointer group"
                onClick={() => triggerRoute('/')}
              >
                <img 
                  src={theme === 'light' ? "/Logo/logo white mode.png" : "/Logo/logo dark mode.png"} 
                  alt="Smart Imagify Logo" 
                  width="24"
                  height="24"
                  className="w-5 h-5 md:w-6 md:h-6 object-contain transition-all duration-500 transform group-hover:rotate-12 group-hover:scale-110 group-hover:brightness-110 filter drop-shadow-sm group-hover:drop-shadow-[0_0_8px_rgba(170,206,22,0.4)]"
                />
                <span className={`font-black text-[15px] md:text-base tracking-tight uppercase transition-all duration-300 bg-clip-text text-transparent bg-gradient-to-r ${
                  theme === 'light'
                    ? 'from-zinc-950 to-zinc-700 group-hover:from-[#aace16] group-hover:to-emerald-500'
                    : 'from-white via-slate-100 to-slate-300 group-hover:from-[#aace16] group-hover:to-[#bdef2b]'
                }`}>
                  Smart Imagify
                </span>
              </div>
            </div>

            {/* Center: Premium Navigation Links */}
            <div className={`hidden md:flex items-center h-9 gap-1 lg:gap-1.5 px-1 rounded-full border transition-all duration-300 ${
              theme === 'light'
                ? 'bg-zinc-100/40 border-zinc-200/50 shadow-[inset_0_1px_2.5px_rgba(0,0,0,0.02)]'
                : 'bg-white/[0.015] border-white/[0.04] shadow-[inset_0_1px_2px_rgba(0,0,0,0.25)]'
            }`}>
              <button
                onMouseEnter={() => handleMouseEnter('tools')}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => handleNavClick(e, '/tools')}
                className={`flex items-center justify-center h-7 gap-1 px-3 rounded-full text-[10px] font-black uppercase tracking-wider transition-all duration-300 relative cursor-pointer ${
                  activeDropdown === 'tools'
                    ? theme === 'light'
                      ? 'bg-white text-zinc-950 shadow-sm'
                      : 'bg-white/10 text-white shadow-lg backdrop-blur-md'
                    : theme === 'light'
                      ? 'text-zinc-600 hover:text-zinc-950 hover:bg-white/60'
                      : 'text-slate-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                <span>Tools</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'tools' ? 'rotate-180' : ''}`} />
              </button>

              <button
                onMouseEnter={() => handleMouseEnter('pricing')}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => handleNavClick(e, '/pricing')}
                className={`flex items-center justify-center h-7 gap-1 px-3 rounded-full text-[10px] font-black uppercase tracking-wider transition-all duration-300 relative cursor-pointer ${
                  activeDropdown === 'pricing'
                    ? theme === 'light'
                      ? 'bg-white text-zinc-950 shadow-sm'
                      : 'bg-white/10 text-white shadow-lg backdrop-blur-md'
                    : theme === 'light'
                      ? 'text-zinc-600 hover:text-zinc-950 hover:bg-white/60'
                      : 'text-slate-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                <span>Pricing</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'pricing' ? 'rotate-180' : ''}`} />
              </button>

              <button
                onMouseEnter={() => handleMouseEnter('resources')}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => handleNavClick(e, '/faq')}
                className={`flex items-center justify-center h-7 gap-1 px-3 rounded-full text-[10px] font-black uppercase tracking-wider transition-all duration-300 relative cursor-pointer ${
                  activeDropdown === 'resources'
                    ? theme === 'light'
                      ? 'bg-white text-zinc-950 shadow-sm'
                      : 'bg-white/10 text-white shadow-lg backdrop-blur-md'
                    : theme === 'light'
                      ? 'text-zinc-600 hover:text-zinc-950 hover:bg-white/60'
                      : 'text-slate-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                <span>Resources</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Right: Actions */}
            <div className="hidden md:flex items-center gap-3">
              {/* Help & FAQs Button */}
              <button 
                onClick={(e) => handleNavClick(e, '/faq')}
                className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all duration-300 cursor-pointer ${
                  theme === 'light' 
                    ? 'border-zinc-200 hover:border-zinc-350 hover:bg-zinc-50 text-zinc-600 hover:text-zinc-950 shadow-sm' 
                    : 'border-white/[0.08] hover:border-white/20 hover:bg-white/[0.03] text-zinc-350 hover:text-white'
                }`}
                title="Help Center"
              >
                <HelpCircle className="w-4 h-4" />
              </button>

              {/* Theme Toggle Button */}
              <button
                type="button"
                onClick={toggleTheme}
                className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all duration-300 cursor-pointer ${
                  theme === 'light' 
                    ? 'border-zinc-200 hover:border-zinc-350 hover:bg-zinc-50 text-zinc-700 hover:text-zinc-950 shadow-sm' 
                    : 'border-white/[0.08] hover:border-white/20 hover:bg-white/[0.03] text-zinc-300 hover:text-white'
                }`}
                title={theme === 'light' ? "Switch to Dark Mode" : "Switch to Light Mode"}
              >
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </button>
              {userProfile ? (
                <div className={`h-9 flex items-center gap-2 px-3 rounded-xl border backdrop-blur-sm ${
                  theme === 'light'
                    ? 'bg-zinc-100 border-zinc-200'
                    : 'border-white/5 bg-white/[0.02]'
                }`}>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#aace16] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#aace16]"></span>
                    </span>
                    <span className={`text-[10px] font-black uppercase tracking-wider ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-200'}`}>
                      {userProfile.name}
                    </span>
                  </div>
                  <div className={`w-[1px] h-3.5 ${theme === 'light' ? 'bg-zinc-300' : 'bg-white/10'}`} />
                  <button 
                    onClick={onSignOut}
                    className={`h-6 flex items-center justify-center px-2 text-[9px] font-black uppercase tracking-wider rounded-lg transition-all duration-300 cursor-pointer ${
                      theme === 'light'
                        ? 'hover:bg-zinc-200 text-zinc-600 hover:text-zinc-950'
                        : 'hover:bg-white/5 text-slate-400 hover:text-white'
                    }`}
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                !(!userProfile && smashedCredits > 0) && (
                  <button 
                    onClick={() => onSignIn(undefined, 'signin')}
                    className={`h-9 px-4 text-xs font-bold uppercase tracking-wider rounded-xl border transition-all duration-300 cursor-pointer ${
                      theme === 'light'
                        ? 'border-zinc-200 hover:border-zinc-350 hover:bg-zinc-50 hover:text-zinc-950 text-zinc-700'
                        : 'border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 text-slate-200 hover:text-white'
                    }`}
                  >
                    Sign In
                  </button>
                )
              )}

              {!userProfile && smashedCredits > 0 ? (
                <div className={`h-9 flex items-center gap-1.5 border rounded-xl p-0.5 transition-all ${
                  theme === 'light'
                    ? 'bg-white border-zinc-200 shadow-sm'
                    : 'bg-zinc-950 border-zinc-800'
                }`}>
                  <div className={`h-7 flex items-center gap-1.5 px-2 rounded-lg border ${
                    theme === 'light'
                      ? 'bg-zinc-100 border-zinc-200 text-zinc-900'
                      : 'bg-zinc-900 border-zinc-800 text-white'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${theme === 'light' ? 'bg-black' : 'bg-white'}`}></span>
                    <span className="text-xs font-bold font-mono">+{smashedCredits} CR</span>
                  </div>
                  <button
                    onClick={() => {
                      try {
                        localStorage.setItem('si_smashed_credits', smashedCredits.toString());
                      } catch {}
                      onSignIn('/dashboard', 'signup');
                    }}
                    className={`h-7 flex items-center justify-center px-3 text-xs font-bold rounded-lg cursor-pointer transition-all active:scale-95 ${
                      theme === 'light'
                        ? 'bg-black hover:bg-zinc-800 text-white'
                        : 'bg-white hover:bg-zinc-200 text-black'
                    }`}
                  >
                    Claim
                  </button>
                </div>
              ) : (
                <button 
                  onClick={onLaunchTool}
                  className={`h-9 px-4 text-xs font-black uppercase tracking-wider rounded-xl transition-all duration-300 cursor-pointer shadow-md hover:shadow-lg hover:shadow-[#aace16]/10 active:scale-[0.98] ${
                    theme === 'light'
                      ? 'bg-black hover:bg-black/90 text-white border border-zinc-800'
                      : 'bg-[#aace16] hover:bg-[#bdef2b] text-black border border-[#aace16]/20'
                  }`}
                >
                  {userProfile ? 'Go to Studio' : 'Launch Studio'}
                </button>
              )}
            </div>

            {/* Mobile Actions and Hamburger Menu */}
            <div className="flex items-center gap-2 md:hidden">
              <button
                type="button"
                onClick={toggleTheme}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                  theme === 'light' 
                    ? 'border-zinc-200 hover:bg-zinc-100 text-zinc-700' 
                    : 'border-zinc-800 hover:bg-zinc-900/60 text-zinc-300'
                }`}
              >
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                  theme === 'light' 
                    ? 'border-zinc-200 hover:bg-zinc-100 text-zinc-700' 
                    : 'border-zinc-800 hover:bg-zinc-900/60 text-zinc-300'
                }`}
              >
                {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Expandable Mobile Panel inside the Glass Card */}
          {mobileMenuOpen && (
            <div className="mt-4 pt-4 border-t border-[var(--border)] flex flex-col gap-3 transition-all duration-300 text-[var(--text-1)]">
              
              {/* Mobile Navigation Links */}
              <nav className="flex flex-col gap-2.5 text-left mb-2">
                <a href="/tools" onClick={(e) => { setMobileMenuOpen(false); handleNavClick(e, '/tools'); }} className={`py-1.5 text-xs font-bold border-b transition-colors flex items-center justify-between ${
                  theme === 'light' ? 'border-zinc-100 text-zinc-800 hover:text-[#aace16]' : 'border-white/[0.04] text-zinc-200 hover:text-[#aace16]'
                }`}>
                  <span>Tools Directory</span>
                  <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-zinc-500" />
                </a>
                <a href="/pricing" onClick={(e) => { setMobileMenuOpen(false); handleNavClick(e, '/pricing'); }} className={`py-1.5 text-xs font-bold border-b transition-colors flex items-center justify-between ${
                  theme === 'light' ? 'border-zinc-100 text-zinc-800 hover:text-[#aace16]' : 'border-white/[0.04] text-zinc-200 hover:text-[#aace16]'
                }`}>
                  <span>Pricing Plans</span>
                  <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-zinc-500" />
                </a>
                <a href="/faq" onClick={(e) => { setMobileMenuOpen(false); handleNavClick(e, '/faq'); }} className={`py-1.5 text-xs font-bold border-b transition-colors flex items-center justify-between ${
                  theme === 'light' ? 'border-zinc-100 text-zinc-800 hover:text-[#aace16]' : 'border-white/[0.04] text-zinc-200 hover:text-[#aace16]'
                }`}>
                  <span>FAQs & Support</span>
                  <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-zinc-500" />
                </a>
              </nav>
              <div className="flex flex-col gap-2 w-full mt-2">
                {userProfile && (
                  <div className={`px-3 py-2 text-center text-xs font-bold border rounded-lg flex items-center justify-center gap-1.5 ${
                    theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-700' : 'bg-zinc-900 border-zinc-800 text-zinc-350'
                  }`}>
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#aace16] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#aace16]"></span>
                    </span>
                    <span>Welcome, {userProfile.name}</span>
                  </div>
                )}

                {userProfile ? (
                  <button 
                    onClick={() => { onSignOut(); setMobileMenuOpen(false); }} 
                    className={`w-full py-2.5 border text-xs font-semibold rounded-lg cursor-pointer ${
                      theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-700' : 'bg-zinc-900 border-zinc-800 text-zinc-350 hover:text-white'
                    }`}
                  >
                    Sign Out
                  </button>
                ) : (
                  !(!userProfile && smashedCredits > 0) && (
                    <button 
                      onClick={() => { onSignIn(undefined, 'signin'); setMobileMenuOpen(false); }} 
                      className={`w-full py-2.5 border text-xs font-semibold rounded-lg cursor-pointer ${
                        theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-700' : 'bg-zinc-900 border-zinc-800 text-zinc-350 hover:text-white'
                      }`}
                    >
                      Sign In
                    </button>
                  )
                )}

                {!userProfile && smashedCredits > 0 ? (
                  <button 
                    onClick={() => {
                      try {
                        localStorage.setItem('si_smashed_credits', smashedCredits.toString());
                      } catch {}
                      onSignIn('/dashboard', 'signup');
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full py-2.5 text-xs font-bold rounded-lg cursor-pointer transition-all active:scale-95 ${
                      theme === 'light' ? 'bg-black text-white hover:bg-zinc-800' : 'bg-white text-black hover:bg-zinc-200'
                    }`}
                  >
                    Claim +{smashedCredits} Credits
                  </button>
                ) : (
                  <button 
                    onClick={() => { onLaunchTool(); setMobileMenuOpen(false); }}
                    className={`w-full py-2.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                      theme === 'light'
                        ? 'bg-black text-white hover:bg-zinc-800'
                        : 'bg-white text-black hover:bg-zinc-200'
                    }`}
                  >
                    {userProfile ? 'Go to Studio' : 'Launch Studio'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Desktop Hover Mega-Menus rendered inline relative to floating header container */}
          <div className="hidden md:block">
            {activeDropdown === 'tools' && (
              <div 
                onMouseEnter={() => handleMouseEnter('tools')}
                onMouseLeave={handleMouseLeave}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[98%] max-w-4xl p-6 rounded-xl border border-[var(--border)] bg-[var(--sidebar-bg)] text-[var(--text-1)] shadow-[var(--shadow-premium)] transition-all duration-300 transform origin-top animate-fade-slide-down z-50"
              >
                <div className="grid grid-cols-12 gap-6 text-left">
                  {/* Left Column: Featured Studio Workspace */}
                  <div className="col-span-4 p-5 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] flex flex-col justify-between transition-all duration-300 hover:bg-[var(--bg-2)]">
                    <div>
                      <div className="inline-flex items-center justify-center p-2.5 rounded-xl bg-[#aace16]/10 text-[#aace16] mb-4">
                        <Sparkles className="w-5 h-5 animate-pulse" />
                      </div>
                      <h3 className="text-[10px] font-black uppercase tracking-widest premium-font-heading text-[#aace16] mb-1">
                        Featured Workspace
                      </h3>
                      <h4 className="text-sm font-extrabold mb-2 text-[var(--text-1)]">
                        Smart Studio Suite
                      </h4>
                      <p className="text-[11px] leading-relaxed text-[var(--text-2)]">
                        Access our full suite of local processing compilers directly from your browser. Zero server lag, unlimited files.
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        setActiveDropdown(null);
                        onLaunchTool();
                      }}
                      className="w-full py-2.5 rounded-lg bg-[#aace16] hover:bg-[#bdef2b] text-black text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 cursor-pointer premium-font-heading shadow-md shadow-[#aace16]/20 active:scale-[0.97]"
                    >
                      <span>{userProfile ? 'Go to Studio' : 'Launch Studio'}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Middle Column: Core Utilities */}
                  <div className="col-span-4 space-y-4">
                    <h4 className={`text-[9px] font-black uppercase tracking-widest premium-font-heading ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                      Core Utilities
                    </h4>
                    <div className="space-y-3">
                      {[
                        { key: 'compress', icon: Zap, label: 'Image Compressor', desc: 'Fast client-side WebP, PNG, JPG & AVIF compression.' },
                        { key: 'bulk', icon: Images, label: 'Bulk Optimizer', desc: 'Batch optimize hundreds of images in parallel.' },
                        { key: 'resize', icon: Crop, label: 'Precision Resizer', desc: 'Perfect custom dimensions, cropping & aspect ratios.' },
                        { key: 'format', icon: RefreshCw, label: 'Format Converter', desc: 'Convert between JPG, PNG, WebP, AVIF.' },
                      ].map((t) => {
                        const Ic = t.icon;
                        return (
                          <a key={t.key}
                            onClick={() => {
                              setActiveDropdown(null);
                              try { localStorage.setItem('si_initial_tab', t.key); } catch {}
                              onLaunchTool();
                            }}
                            className={`group flex items-start gap-3 p-1.5 rounded-lg transition-all cursor-pointer ${theme === 'light' ? 'hover:bg-zinc-100' : 'hover:bg-white/[0.03]'}`}
                          >
                            <div className="p-2 rounded-lg bg-zinc-500/10 text-zinc-400 group-hover:text-[#aace16] group-hover:bg-[#aace16]/10 transition-colors shrink-0">
                              <Ic className="w-4 h-4" />
                            </div>
                            <div>
                              <h5 className={`text-xs font-bold transition-colors ${theme === 'light' ? 'text-zinc-950 group-hover:text-[#aace16]' : 'text-white group-hover:text-[#aace16]'}`}>
                                {t.label}
                              </h5>
                              <p className={`text-[10px] leading-snug mt-0.5 ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                {t.desc}
                              </p>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Column: AI & Creative Tools */}
                  <div className="col-span-4 space-y-4">
                    <h4 className={`text-[9px] font-black uppercase tracking-widest premium-font-heading ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                      AI & Creative Suite
                    </h4>
                    <div className="space-y-3">
                      {[
                        { key: 'passport', icon: IdCard, label: 'Passport Photo Maker', desc: 'Generate standard biometric passport sizes.', badge: 'PRO' },
                        { key: 'collage', icon: Grid3X3, label: 'Collage Creator', desc: 'Make stunning custom layouts & templates.', badge: 'Hot' },
                        { key: 'ai', icon: Sparkles, label: 'AI Object Eraser', desc: 'Clean objects, marks and timestamps offline.', badge: 'AI' },
                        { key: 'stock', icon: Camera, label: 'Stock Library', desc: 'Find 10,000+ free high resolution images.' },
                      ].map((t) => {
                        const Ic = t.icon;
                        return (
                          <a key={t.key}
                            onClick={() => {
                              setActiveDropdown(null);
                              try { localStorage.setItem('si_initial_tab', t.key); } catch {}
                              onLaunchTool();
                            }}
                            className={`group flex items-start gap-3 p-1.5 rounded-lg transition-all cursor-pointer ${theme === 'light' ? 'hover:bg-zinc-100' : 'hover:bg-white/[0.03]'}`}
                          >
                            <div className="p-2 rounded-lg bg-zinc-500/10 text-zinc-400 group-hover:text-[#aace16] group-hover:bg-[#aace16]/10 transition-colors shrink-0">
                              <Ic className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <h5 className={`text-xs font-bold transition-colors truncate ${theme === 'light' ? 'text-zinc-950 group-hover:text-[#aace16]' : 'text-white group-hover:text-[#aace16]'}`}>
                                  {t.label}
                                </h5>
                                {t.badge && (
                                  <span className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.2 rounded ${
                                    t.badge === 'AI' || t.badge === 'PRO'
                                      ? 'bg-emerald-500/10 text-emerald-500'
                                      : 'bg-[#aace16]/15 text-[#aace16]'
                                  }`}>
                                    {t.badge}
                                  </span>
                                )}
                              </div>
                              <p className={`text-[10px] leading-snug mt-0.5 ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                {t.desc}
                              </p>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeDropdown === 'pricing' && (
              <div 
                onMouseEnter={() => handleMouseEnter('pricing')}
                onMouseLeave={handleMouseLeave}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[98%] max-w-3xl p-6 rounded-xl border border-[var(--border)] bg-[var(--sidebar-bg)] text-[var(--text-1)] shadow-[var(--shadow-premium)] transition-all duration-300 transform origin-top animate-fade-slide-down z-50"
              >
                <div className="grid grid-cols-12 gap-4 text-left">
                  {/* Info Column */}
                  <div className="col-span-3 pr-2 flex flex-col justify-between">
                    <div>
                      <h4 className={`text-[9px] font-black uppercase tracking-widest premium-font-heading ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                        Flexible Plans
                      </h4>
                      <h3 className={`text-base font-extrabold mt-1.5 leading-snug ${theme === 'light' ? 'text-zinc-950' : 'text-white'}`}>
                        Premium Features, Local Execution.
                      </h3>
                      <p className={`text-[10px] leading-relaxed mt-2 ${theme === 'light' ? 'text-zinc-600' : 'text-zinc-400'}`}>
                        Processing runs locally inside your browser sandbox. Upgrading grants advanced compilers and unlimited limits.
                      </p>
                    </div>
                    <a
                      onClick={(e) => {
                        setActiveDropdown(null);
                        handleNavClick(e, '/pricing');
                      }}
                      className="text-[10px] font-bold text-[#aace16] hover:text-[#bdef2b] flex items-center gap-1 cursor-pointer"
                    >
                      <span>Compare all details</span>
                      <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>

                  {/* Plan 1 */}
                  {/* Plan 1 */}
                  <div className="col-span-3 p-4 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] flex flex-col justify-between">
                    <div>
                      <span className="text-[8px] font-black uppercase tracking-widest text-[var(--text-3)]">
                        Starter
                      </span>
                      <div className="text-lg font-bold mt-1 text-[var(--text-1)]">
                        Free
                      </div>
                      <p className="text-[10px] leading-snug mt-1 text-[var(--text-2)]">
                        Essential core processing tools for individual files.
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        setActiveDropdown(null);
                        onLaunchTool();
                      }}
                      className="w-full py-1.5 text-[10px] font-bold rounded mt-4 cursor-pointer transition-all bg-[var(--bg-2)] hover:bg-[var(--bg-3)] border border-[var(--border)] text-[var(--text-1)]"
                    >
                      {userProfile ? 'Go to Studio' : 'Launch Free'}
                    </button>
                  </div>

                  {/* Plan 2 */}
                  <div className="col-span-3 p-4 rounded-lg border border-[#aace16]/30 bg-[#aace16]/5 flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 px-2 py-0.5 bg-[#aace16] text-[7px] text-black font-black uppercase tracking-wider rounded-bl">
                      PRO
                    </div>
                    <div>
                      <span className="text-[8px] font-black uppercase tracking-widest text-[#aace16]">
                        Most Popular
                      </span>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-lg font-bold text-[var(--text-1)]">$9.99</span>
                        <span className="text-[9px] text-zinc-500">/ mo</span>
                      </div>
                      <p className="text-[10px] leading-snug mt-1 text-[var(--text-2)]">
                        Full access to offline AI Eraser, batch suites, and export presets.
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        setActiveDropdown(null);
                        onPlanSelect('Premium Pro');
                      }}
                      className="w-full py-1.5 text-[10px] font-black uppercase tracking-widest rounded mt-4 cursor-pointer transition-all bg-[#aace16] hover:bg-[#bdef2b] text-black border border-[#aace16]/20"
                    >
                      Upgrade Pro
                    </button>
                  </div>

                  {/* Plan 3 */}
                  <div className="col-span-3 p-4 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] flex flex-col justify-between">
                    <div>
                      <span className="text-[8px] font-black uppercase tracking-widest text-[var(--text-3)]">
                        Organization
                      </span>
                      <div className="text-lg font-bold mt-1 text-[var(--text-1)]">
                        Custom
                      </div>
                      <p className="text-[10px] leading-snug mt-1 text-[var(--text-2)]">
                        SLA agreements, user licensing seats, and customized integration.
                      </p>
                    </div>
                    <button 
                      onClick={(e) => {
                        setActiveDropdown(null);
                        handleNavClick(e, '/pricing');
                      }}
                      className="w-full py-1.5 text-[10px] font-bold rounded mt-4 cursor-pointer transition-all bg-[var(--bg-2)] hover:bg-[var(--bg-3)] border border-[var(--border)] text-[var(--text-1)]"
                    >
                      Contact Sales
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeDropdown === 'resources' && (
              <div 
                onMouseEnter={() => handleMouseEnter('resources')}
                onMouseLeave={handleMouseLeave}
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[98%] max-w-3xl p-6 rounded-2xl border shadow-2xl backdrop-blur-2xl transition-all duration-300 transform origin-top animate-fade-slide-down z-50 ${
                  theme === 'light'
                    ? 'bg-white/95 border-zinc-200/80 text-zinc-900 shadow-zinc-300/40'
                    : 'bg-zinc-950/90 border-white/10 text-zinc-200 shadow-black/80'
                }`}
              >
                <div className="grid grid-cols-12 gap-6 text-left">
                  {/* Help Desk */}
                  <div className="col-span-4 space-y-4">
                    <h4 className={`text-[9px] font-black uppercase tracking-widest premium-font-heading ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                      Support Center
                    </h4>
                    <div className="space-y-3">
                      <a 
                        onClick={(e) => {
                          setActiveDropdown(null);
                          handleNavClick(e, '/faq');
                        }}
                        className={`group flex items-start gap-3 p-1.5 rounded-lg transition-all cursor-pointer ${theme === 'light' ? 'hover:bg-zinc-100' : 'hover:bg-white/[0.03]'}`}
                      >
                        <div className="p-2 rounded-lg bg-zinc-500/10 text-zinc-400 group-hover:text-[#aace16] transition-colors shrink-0">
                          <HelpCircle className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className={`text-xs font-bold transition-colors ${theme === 'light' ? 'text-zinc-950 group-hover:text-[#aace16]' : 'text-white group-hover:text-[#aace16]'}`}>
                            Common FAQs
                          </h5>
                          <p className={`text-[10px] leading-snug mt-0.5 ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            Resolve queries about billing and offline capabilities.
                          </p>
                        </div>
                      </a>
                    </div>
                  </div>

                  {/* Technical Docs */}
                  <div className="col-span-4 space-y-4">
                    <h4 className={`text-[9px] font-black uppercase tracking-widest premium-font-heading ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                      Documentation
                    </h4>
                    <div className="space-y-3">
                      <a 
                        onClick={(e) => {
                          setActiveDropdown(null);
                          handleNavClick(e, '/faq');
                        }}
                        className={`group flex items-start gap-3 p-1.5 rounded-lg transition-all cursor-pointer ${theme === 'light' ? 'hover:bg-zinc-100' : 'hover:bg-white/[0.03]'}`}
                      >
                        <div className="p-2 rounded-lg bg-zinc-500/10 text-zinc-400 group-hover:text-[#aace16] transition-colors shrink-0">
                          <BookOpen className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className={`text-xs font-bold transition-colors ${theme === 'light' ? 'text-zinc-950 group-hover:text-[#aace16]' : 'text-white group-hover:text-[#aace16]'}`}>
                            Technology & Safety
                          </h5>
                          <p className={`text-[10px] leading-snug mt-0.5 ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            Learn how we process your images privately and securely in your browser.
                          </p>
                        </div>
                      </a>
                    </div>
                  </div>

                  {/* System Status */}
                  <div className="col-span-4 space-y-4">
                    <h4 className={`text-[9px] font-black uppercase tracking-widest premium-font-heading ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                      System Health
                    </h4>
                    <div className="space-y-3">
                      <div className={`p-4 rounded-xl border ${
                        theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/[0.01] border-white/5'
                      }`}>
                        <div className="flex items-center gap-2">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                          </span>
                          <span className={`text-xs font-bold ${theme === 'light' ? 'text-zinc-800' : 'text-zinc-300'}`}>
                            All Compilers Operational
                          </span>
                        </div>
                        <p className={`text-[10px] leading-snug mt-2 ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                          All local modules, codecs, and background removal models are operating optimally.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>
      </div>

      {/* 2. CORE VIEWPORT HERO */}
      {/* Calculate main element padding dynamically based on whether top announcement bar is open */}
      <main className={`flex-grow relative z-10 flex flex-col items-center pb-0 w-full transition-all duration-300 ${
        (isTopBarOpen && topBarConfig?.enabled && topBarConfig?.showOnLanding && topBarConfig?.slides?.length > 0)
          ? 'pt-[124px] sm:pt-[140px]' 
          : 'pt-20 sm:pt-24'
      }`}>
        {specialContent ? (
          <div className="w-full">
            {specialContent}
          </div>
        ) : (
          <div className="max-w-[1260px] mx-auto px-6 md:px-8 pt-4 md:pt-8 pb-0 flex flex-col items-center text-center relative w-full">
            
            {/* Top Text Content Block */}
            <div className="max-w-5xl mx-auto space-y-5 flex flex-col items-center">
              
              {/* Dynamic Slider Headline & Subtitle */}
              <div key={`slide-text-${currentSlideIdx}`} className="space-y-4 animate-slide-push flex flex-col items-center justify-center min-h-[220px] sm:min-h-[180px] md:min-h-[160px] lg:min-h-[140px]">
                {/* Premium Headline */}
                <h1 className={`landing-mascot-target text-[24px] sm:text-3xl md:text-[38px] lg:text-[44px] xl:text-[48px] font-bold tracking-[-0.02em] leading-[1.12] max-w-4xl mx-auto premium-font-heading ${theme === 'light' ? 'text-zinc-950' : 'text-white'}`}>
                  {activeSlide?.hero_title ? renderFormattedTitle(activeSlide.hero_title) : (
                    <>
                      Edit, Compress & Enhance Photos <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>right in your browser</span>
                    </>
                  )}
                </h1>

                {/* Description — consumer-friendly, no tech jargon */}
                <p className={`text-sm sm:text-[15px] md:text-base font-normal leading-[1.7] max-w-2xl mx-auto ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {activeSlide?.hero_subtitle || 'Resize, remove backgrounds, create collages, and convert formats — all without uploading your files anywhere. Fast, free, and completely private.'}
                </p>
              </div>

              {/* Centered CTA Row */}
              <div key={`slide-cta-${currentSlideIdx}`} className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-slide-push w-full max-w-md mx-auto pt-1">
                <button 
                  onClick={() => {
                    if (activeSlide?.hero_cta_link) {
                      if (activeSlide.hero_cta_link.startsWith('/')) {
                        onLaunchTool();
                      } else {
                        window.open(activeSlide.hero_cta_link, '_blank');
                      }
                    } else {
                      onLaunchTool();
                    }
                  }}
                  className="landing-mascot-target w-full sm:w-auto px-7 py-3 rounded-xl bg-[#aace16] hover:bg-[#bdef2b] text-black text-[11px] font-bold uppercase tracking-[0.12em] transition-all flex items-center justify-center gap-2.5 hover:scale-[1.03] active:scale-[0.98] cursor-pointer premium-font-heading"
                >
                  <span>{userProfile ? 'Go to Studio' : (activeSlide?.hero_cta_text || 'Start Editing — Free')}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => onSignIn(undefined, 'signup')}
                  className={`w-full sm:w-auto px-6 py-3 rounded-xl border text-[11px] font-bold uppercase tracking-[0.08em] transition-all cursor-pointer text-center premium-font-heading ${
                    theme === 'light'
                      ? 'border-zinc-200 hover:border-zinc-400 bg-white hover:bg-zinc-50 text-zinc-900'
                      : 'border-white/10 hover:border-white/25 bg-white/[0.04] hover:bg-white/[0.08] text-white'
                  }`}
                >
                  Create Free Account
                </button>
              </div>

              {/* Slider Dots Navigation */}
              {heroSlides.length > 1 && (
                <div className={`w-fit px-4 py-1.5 flex items-center gap-2.5 rounded-full backdrop-blur-md select-none mx-auto mt-1 ${theme === 'light' ? 'bg-black/5 border border-black/5' : 'bg-white/[0.03] border border-white/5'}`}>
                  {heroSlides.map((_, idx) => (
                    <button
                      key={`dot-${idx}`}
                      onClick={() => setCurrentSlideIdx(idx)}
                      className={`h-2 rounded-full transition-all duration-500 cursor-pointer relative ${
                        idx === currentSlideIdx 
                          ? 'w-7 bg-[#aace16] shadow-[0_0_8px_rgba(170,206,22,0.8)]' 
                          : (theme === 'light' ? 'w-2 bg-black/25 hover:bg-black/45' : 'w-2 bg-white/25 hover:bg-white/45')
                      }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    >
                      {idx === currentSlideIdx && (
                        <span className="absolute inset-0 rounded-full bg-[#aace16] blur-[3px] opacity-40" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 3D STACKED MOCKUPS SCREENSHOTS */}
            <div className="screenshots-fade-mask w-full max-w-[1100px] mx-auto mt-6 sm:mt-8 relative select-none px-4 md:px-0 z-20">
              
              {/* Outer perspective wrapper */}
              <div 
                className="relative w-full aspect-[16/8.5] sm:aspect-[16/8] flex items-center justify-center"
                style={{
                  perspective: '2000px',
                }}
              >
                {/* Premium background radial gradient spotlight behind mockups */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[75%] h-[75%] rounded-full bg-[radial-gradient(circle,_rgba(170,206,22,0.06)_0%,_transparent_70%)] dark:bg-[radial-gradient(circle,_rgba(170,206,22,0.09)_0%,_transparent_70%)] pointer-events-none blur-3xl z-0" />

                {/* Left Mockup */}
                <motion.div 
                  initial={{ opacity: 0, x: -60, y: 20 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute left-[5%] w-[48%] z-10"
                  style={{
                    transform: 'rotateY(16deg) rotateX(3deg) rotateZ(-2deg) translateZ(-60px) translateX(-5%)',
                    transformOrigin: 'left center',
                  }}
                >
                  <div className={`rounded-xl overflow-hidden transition-all duration-700 hover:scale-[1.03] hover:z-30 animate-float-1 ${
                    theme === 'light' 
                      ? 'border border-zinc-200 shadow-md' 
                      : 'border border-white/10 shadow-[0_0_40px_rgba(255,255,255,0.03),0_10px_30px_rgba(0,0,0,0.6)]'
                  }`}>
                    <img 
                      src="/dashboard_mockup_left.png" 
                      alt="Dashboard Left Panel" 
                      className="w-full h-auto select-none pointer-events-none object-cover"
                    />
                  </div>
                </motion.div>

                {/* Right Mockup */}
                <motion.div 
                  initial={{ opacity: 0, x: 60, y: 20 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute right-[5%] w-[48%] z-10"
                  style={{
                    transform: 'rotateY(-16deg) rotateX(3deg) rotateZ(2deg) translateZ(-60px) translateX(5%)',
                    transformOrigin: 'right center',
                  }}
                >
                  <div className={`rounded-xl overflow-hidden transition-all duration-700 hover:scale-[1.03] hover:z-30 animate-float-3 ${
                    theme === 'light' 
                      ? 'border border-zinc-200 shadow-md' 
                      : 'border border-white/10 shadow-[0_0_40px_rgba(255,255,255,0.03),0_10px_30px_rgba(0,0,0,0.6)]'
                  }`}>
                    <img 
                      src="/dashboard_mockup_right.png" 
                      alt="Dashboard Right Panel" 
                      className="w-full h-auto select-none pointer-events-none object-cover"
                    />
                  </div>
                </motion.div>

                {/* Center Mockup - Highlighted */}
                <motion.div 
                  initial={{ opacity: 0, y: 80, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute w-[68%] z-20"
                  style={{
                    transform: 'perspective(2000px) rotateX(4deg) translateZ(40px)',
                  }}
                >
                  <div className={`rounded-2xl overflow-hidden transition-all duration-700 hover:scale-[1.025] animate-float-2 ${
                    theme === 'light' 
                      ? 'border border-zinc-300 shadow-xl' 
                      : 'border border-white/15 shadow-[0_0_50px_rgba(170,206,22,0.12),0_20px_50px_rgba(0,0,0,0.8)]'
                  }`}>
                    <img 
                      src="/dashboard_mockup.png" 
                      alt="Smart Imagify Dashboard — Your complete image editing studio" 
                      className="w-full h-auto select-none pointer-events-none"
                    />
                  </div>
                </motion.div>


                {/* Bottom fade gradient overlay for the entire screenshot section */}
                <div className="absolute inset-x-0 bottom-0 h-28 sm:h-36 pointer-events-none z-30" style={{
                  background: theme === 'light'
                    ? 'linear-gradient(to top, rgba(250,250,255,1) 0%, rgba(250,250,255,0.85) 20%, rgba(250,250,255,0.3) 60%, transparent 100%)'
                    : 'linear-gradient(to top, #040406 0%, rgba(4,4,6,0.85) 20%, rgba(4,4,6,0.3) 60%, transparent 100%)'
                }} />
              </div>
            </div>

          </div>
        )}

        {/* ANIMATED STATS COUNTER */}
        {!specialContent && (
          <div className={`w-full max-w-[1100px] mx-auto px-6 md:px-8 py-10 sm:py-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 select-none z-20 relative ${
            theme === 'light' ? '' : ''
          }`}>
            {[
              { value: '10+', label: 'Creative Tools', icon: <Zap className="w-4 h-4" /> },
              { value: '50K+', label: 'Happy Users', icon: <Sparkles className="w-4 h-4" /> },
              { value: '100%', label: 'Private & Offline', icon: <Shield className="w-4 h-4" /> },
              { value: '0', label: 'Files Uploaded', icon: <Lock className="w-4 h-4" /> },
            ].map((stat, idx) => (
              <div key={`stat-${idx}`} className="flex flex-col items-center text-center gap-2">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-1 ${
                  theme === 'light' ? 'bg-zinc-100 text-zinc-500' : 'bg-white/[0.05] text-zinc-400'
                }`}>
                  {stat.icon}
                </div>
                <span className={`text-2xl sm:text-3xl font-black font-mono tracking-tight ${
                  theme === 'light' ? 'text-zinc-900' : 'text-white'
                }`}>
                  {stat.value}
                </span>
                <span className={`text-[10px] sm:text-[11px] font-semibold uppercase tracking-widest ${
                  theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'
                }`}>
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* PARTNER LOGOS SCROLLING MARQUEE Ribbon */}
        {!specialContent && (
          <div className={`w-full py-8 overflow-hidden relative z-20 select-none border-t border-b ${
            theme === 'light' ? 'border-zinc-100 bg-zinc-50/50' : 'border-white/[0.04] bg-white/[0.01]'
          }`}>
            {/* Edge Fade Gradients */}
            <div className="absolute inset-0 z-20 pointer-events-none" style={{
              background: theme === 'light'
                ? 'linear-gradient(to right, #f8f9fc 0%, transparent 15%, transparent 85%, #f8f9fc 100%)'
                : 'linear-gradient(to right, #09090b 0%, transparent 15%, transparent 85%, #09090b 100%)'
            }} />

            <p className={`text-center text-[9px] font-mono uppercase tracking-[0.3em] font-bold mb-5 ${
              theme === 'light' ? 'text-zinc-400' : 'text-zinc-600'
            }`}>
              Trusted by creative teams worldwide
            </p>
            
            <div className="w-full overflow-hidden relative">
              <div className="flex gap-16 sm:gap-20 items-center animate-marquee-scroll whitespace-nowrap w-max">
                {[
                  'Google', 'Microsoft', 'Meta', 'Adobe', 'Amazon', 'Nvidia', 'Intel', 'Netflix', 'Shopify', 'Slack',
                  'Google', 'Microsoft', 'Meta', 'Adobe', 'Amazon', 'Nvidia', 'Intel', 'Netflix', 'Shopify', 'Slack'
                ].map((name, idx) => (
                  <span 
                    key={`logo-${idx}`} 
                    className={`text-sm md:text-base font-bold tracking-wider premium-font-heading ${
                      theme === 'light' ? 'text-zinc-350' : 'text-zinc-700'
                    }`}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* 3. CENTERED MINIMAL CONTENT BLOCKS */}
      {!specialContent && (
        <div className={`w-full relative z-10 pb-32 transition-colors duration-300 ${
          theme === 'light' ? 'bg-[#f8f9fc] text-slate-900' : 'bg-[#09090b] text-[#fafafa]'
        }`}>
          <div className="max-w-[1260px] mx-auto px-6 md:px-8 py-20 space-y-36">

            {/* Section A: Everything You Need in One Place */}
            <section className="space-y-14 animate-fade-in relative">
              <div className="text-center max-w-3xl mx-auto space-y-4">
                <span className={`text-[10px] font-mono uppercase tracking-[0.25em] font-bold ${theme === 'light' ? 'text-zinc-500' : 'text-[#aace16]'}`}>
                  Smart Imagify Workspace Directory
                </span>
                <h2 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] ${
                  theme === 'light' ? 'text-zinc-950' : 'text-white'
                }`}>
                  Professional Tools <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>unified inside</span><br />
                  <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>one secure local sandbox</span>
                </h2>
                <p className={`text-xs md:text-sm font-semibold leading-relaxed max-w-2xl mx-auto ${theme === 'light' ? 'text-zinc-600' : 'text-zinc-400'}`}>
                  Access our complete suite of hardware-accelerated image utilities. Every tool runs 100% client-side in your browser, processing your files with desktop-class speed and absolute privacy—no server uploads, no queues, and no data leaks.
                </p>
              </div>

              {/* Infinite Horizontal Carousel */}
              {(() => {
                const TOOLS = [
                  { key: 'ai', title: 'AI Image Generator', desc: 'Create high-resolution artwork and design assets using advanced Flux.2 neural networks running entirely in your browser.', badge: 'AI Neural', icon: Sparkles, specs: 'Text-to-Image · WebGPU' },
                  { key: 'passport', title: 'Passport Photo Maker', desc: 'Generate official biometric ID sheets with smart face positioning, auto background removal, and print-ready layouts.', badge: 'Smart Portrait', icon: IdCard, specs: 'Sizing Guidelines · Print Grid' },
                  { key: 'compress', title: 'Single Compressor', desc: 'Compress WebP, PNG, JPG, and AVIF images by up to 90% using highly optimized client-side WASM engines.', badge: 'Fast WASM', icon: Zap, specs: 'Quality Sliders · Lossless' },
                  { key: 'bulk', title: 'Bulk Optimizer', desc: 'Process and compress hundreds of images in parallel without server queues, upload limits, or waiting.', badge: 'Parallel CPU', icon: Images, specs: 'Batch Conversion · Zip Pack' },
                  { key: 'format', title: 'Format Converter', desc: 'Instantly convert images between JPG, PNG, WebP, AVIF, and high-fidelity vector graphics (SVG).', badge: 'Universal', icon: RefreshCw, specs: 'Batch Format Conversion' },
                  { key: 'resize', title: 'Precision Resizer', desc: 'Adjust exact canvas boundaries, scale dimensions, and crop aspect ratios for web and print layouts.', badge: 'Pixel Perfect', icon: Maximize2, specs: 'Aspect Locks · Scale %' },
                  { key: 'collage', title: 'Collage Maker', desc: 'Merge multiple photos into gorgeous layouts with 300+ premium templates, organic shapes, and drag-and-drop.', badge: 'Art Grid', icon: Grid3X3, specs: 'Drag-and-Drop · Presets' },
                  { key: 'stock', title: 'Stock Photo Library', desc: 'Access and download thousands of high-definition stock photos directly to your project canvas.', badge: 'Free Assets', icon: Camera, specs: 'Unlimited Images · Searchable' },
                ];
                const extendedTools = [...TOOLS, ...TOOLS, ...TOOLS];
                const cardWidth = windowWidth < 640 ? 280 : 380;
                const gap = 24;

                return (
                  <div className="relative w-full pt-6 pb-2">

                    {/* Outer Viewport Wrapper stretching full screen width */}
                    <div 
                      ref={containerRef}
                      className="relative w-screen ml-[calc(50%-50vw)] mr-[calc(50%-50vw)] overflow-hidden py-8 select-none"
                      onMouseEnter={() => setIsAutoplayPaused(true)}
                      onMouseLeave={() => setIsAutoplayPaused(false)}
                    >
                      {/* Inner track containing all cards */}
                      <div 
                        ref={trackRef}
                        onTransitionEnd={handleTransitionEnd}
                        className="flex"
                        style={{
                          gap: `${gap}px`,
                          transform: `translate3d(${-activeToolIdx * (cardWidth + gap) + (windowWidth - cardWidth) / 2}px, 0, 0)`,
                          width: `${extendedTools.length * (cardWidth + gap)}px`,
                          transition: transitionDuration > 0 ? `transform ${transitionDuration}ms cubic-bezier(0.16, 1, 0.3, 1)` : 'none',
                          willChange: 'transform',
                        }}
                      >
                        {extendedTools.map((tool, idx) => {
                          const Icon = tool.icon;
                          const distance = Math.abs(idx - activeToolIdx);
                          
                          // Snapping layout configurations with high opacity to prevent dark washouts
                          let scaleClass = '';
                          let isCenter = false;
                          if (distance === 0) {
                            scaleClass = 'scale-105 opacity-100 z-20';
                            isCenter = true;
                          } else if (distance === 1) {
                            scaleClass = 'scale-95 opacity-90 hover:opacity-100 z-10';
                          } else if (distance === 2) {
                            scaleClass = 'scale-85 opacity-80 hover:opacity-90 z-5';
                          } else {
                            scaleClass = 'scale-75 opacity-70 hover:opacity-85 z-0';
                          }

                          return (
                            <div
                              key={`${tool.key}-${idx}`}
                              onClick={() => {
                                if (!isCenter) {
                                  triggerManualSlide(idx);
                                } else {
                                  try { localStorage.setItem('si_initial_tab', tool.key); } catch {}
                                  onLaunchTool();
                                }
                              }}
                              className={`relative shrink-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${scaleClass}`}
                              style={{
                                width: `${cardWidth}px`,
                                cursor: 'pointer',
                              }}
                            >
                              {/* Premium Card inspired by HubTab Component */}
                              <div className={`group p-6 rounded-xl border flex flex-col justify-between h-[320px] transition-all duration-300 relative overflow-hidden bg-[var(--sidebar-bg)] ${
                                isCenter
                                  ? 'border-[#aace16]/45 bg-[var(--bg-3)]'
                                  : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                              }`}>
                                {/* Card Header / Title Info */}
                                <div className="relative z-10 space-y-4">
                                  <div className="flex items-center justify-between">
                                    <div className="w-9 h-9 rounded bg-[var(--bg-2)] border border-[var(--border)] text-[#aace16] flex items-center justify-center">
                                      <Icon className="w-5 h-5" />
                                    </div>
                                    <span className="text-[8.5px] font-mono font-bold tracking-wider px-1.5 py-0.5 rounded bg-[var(--bg-3)] border border-[var(--border)] text-[var(--text-3)] uppercase leading-none">
                                      {tool.badge}
                                    </span>
                                  </div>
                                  <div className="space-y-2 text-left">
                                    <h3 className="text-sm font-extrabold tracking-wide text-[var(--text-1)]">
                                      {tool.title}
                                    </h3>
                                    <p className={`text-[11px] leading-relaxed text-[var(--text-2)] transition-opacity duration-500 ${isCenter ? 'opacity-100' : 'opacity-70'}`}>
                                      {tool.desc}
                                    </p>
                                  </div>
                                </div>

                                {/* Card Footer with Launch Button & Specs */}
                                <div className="relative z-10 space-y-4 mt-auto">
                                  {/* Launch Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      try { localStorage.setItem('si_initial_tab', tool.key); } catch {}
                                      onLaunchTool();
                                    }}
                                    className={`w-full py-2 text-[9.5px] font-black uppercase tracking-widest rounded-lg transition-all duration-300 flex items-center justify-center gap-1.5 cursor-pointer border ${
                                      isCenter
                                        ? 'bg-[#aace16] hover:bg-[#bdef2b] text-black border-[#aace16]/20'
                                        : 'bg-[var(--bg-2)] hover:bg-[var(--bg-3)] border border-[var(--border)] text-[var(--text-1)]'
                                    }`}
                                  >
                                    <span>{userProfile ? 'Go to Workspace' : 'Launch Workspace'}</span>
                                    <ArrowRight className="w-3.5 h-3.5" />
                                  </button>

                                  <div className="h-[1px] w-full bg-[var(--border)]/60" />
                                  
                                  {/* Centered Specs text */}
                                  <div className="text-center text-[9px] font-bold uppercase tracking-wider text-[var(--text-3)] select-none">
                                    {tool.specs}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center justify-center gap-4 mt-6 select-none">
                      <button
                        onClick={() => triggerManualSlide(activeToolIdx - 1)}
                        className="w-8 h-8 rounded-full border border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-1)] hover:bg-[var(--bg-2)] flex items-center justify-center transition-all cursor-pointer hover:scale-105 active:scale-95"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                      </button>

                      <div className="flex items-center gap-1.5">
                        {TOOLS.map((_, idx) => (
                          <button
                            key={'dot-' + idx}
                            onClick={() => triggerManualSlide(8 + idx)}
                            className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${
                              (activeToolIdx % 8) === idx
                                ? 'w-5 bg-[#aace16]'
                                : 'w-1.5 bg-[var(--border)] hover:bg-[var(--text-3)]'
                            }`}
                          />
                        ))}
                      </div>

                      <button
                        onClick={() => triggerManualSlide(activeToolIdx + 1)}
                        className="w-8 h-8 rounded-full border border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-1)] hover:bg-[var(--bg-2)] flex items-center justify-center transition-all cursor-pointer hover:scale-105 active:scale-95"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                      </button>
                    </div>
                  </div>
                );
              })()}
            </section>

            {/* Why Local-First: Clean Visual Showcase */}
            <section className="space-y-16">
              <div className="text-center max-w-3xl mx-auto space-y-4">
                <span className={`text-[9px] font-mono uppercase tracking-[0.25em] font-black ${theme === 'light' ? 'text-zinc-500' : 'text-[#aace16]'}`}>
                  Why Local-First Processing
                </span>
                <h2 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] ${
                  theme === 'light' ? 'text-zinc-950' : 'text-white'
                }`}>
                  Your Files <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>remain secure</span><br />
                  <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>inside your own local device</span>
                </h2>
                <p className={`text-xs md:text-sm font-semibold leading-relaxed max-w-2xl mx-auto ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  Unlike cloud-based editors, Smart Imagify processes everything inside your browser. No uploads, no servers, no privacy compromises.
                </p>
              </div>

              {/* Sleek Versus Dashboard Split-View Comparison */}
              {(() => {
                const COMPARISONS = [
                  {
                    feature: 'Data Security',
                    cloud: 'Uploaded to cloud database servers',
                    local: 'Processed 100% locally on your computer',
                  },
                  {
                    feature: 'Performance',
                    cloud: 'Slow network uploads & queue lag',
                    local: 'Instant WASM engine, zero latency',
                  },
                  {
                    feature: 'Total Cost',
                    cloud: 'Recurring monthly subscription fees',
                    local: 'Free core tools, optional single Pro key',
                  },
                  {
                    feature: 'Data Leak Risk',
                    cloud: 'High risk on shared remote storage',
                    local: 'Absolute hardware isolated protection',
                  },
                  {
                    feature: 'Offline Mode',
                    cloud: 'Internet connection is strictly required',
                    local: 'Works fully offline once page loads',
                  },
                  {
                    feature: 'File Rules',
                    cloud: 'Arbitrary limits and size blockades',
                    local: 'No artificial size bounds — raw performance',
                  }
                ];

                return (
                  <div className={`rounded-[1.5rem] border p-6 md:p-8 relative overflow-hidden max-w-4xl mx-auto shadow-xl ${
                    theme === 'light'
                      ? 'bg-gradient-to-b from-white via-zinc-50 to-zinc-100/50 border-zinc-200 shadow-sm'
                      : 'bg-gradient-to-b from-[#141418] via-[#0b0b0e] to-[#040406] border-white/[0.08] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)]'
                  }`}>
                    {/* Top ambient flare line */}
                    <div className={`absolute top-0 left-1/6 right-1/6 h-[1.5px] bg-gradient-to-r from-transparent ${
                      theme === 'light' ? 'via-black/15' : 'via-white/30'
                    } to-transparent pointer-events-none z-10`} />

                    {/* Tech Dot Grid Backplate */}
                    <div className="absolute inset-y-0 right-0 w-[45%] opacity-[0.03] pointer-events-none select-none z-0" style={{
                      backgroundImage: theme === 'light'
                        ? 'linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)'
                        : 'linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)',
                      backgroundSize: '14px 14px',
                      maskImage: 'radial-gradient(circle at top right, black 15%, rgba(0,0,0,0.4) 55%, transparent 100%)',
                      WebkitMaskImage: 'radial-gradient(circle at top right, black 15%, rgba(0,0,0,0.4) 55%, transparent 100%)'
                    }} />

                    {/* Comparison Header */}
                    <div className="grid grid-cols-1 md:grid-cols-7 gap-4 pb-4 border-b border-zinc-250/50 dark:border-white/5 text-[9px] font-black uppercase tracking-widest text-zinc-500 z-10 relative select-none">
                      <div className="md:col-span-3 text-left pl-2">The Cloud Way</div>
                      <div className="hidden md:block md:col-span-1 text-center">Benchmark</div>
                      <div className="md:col-span-3 text-left md:text-right pr-2">The Smart Imagify Way</div>
                    </div>

                    {/* Comparison List */}
                    <div className="divide-y divide-zinc-250/20 dark:divide-white/5 z-10 relative">
                      {COMPARISONS.map((row, idx) => (
                        <div 
                          key={'comp-row-'+idx} 
                          className="grid grid-cols-1 md:grid-cols-7 gap-3 py-4 items-center transition-colors duration-300 hover:bg-zinc-100/[0.02] dark:hover:bg-white/[0.005]"
                        >
                          {/* Left Column: Cloud Way */}
                          <div className="md:col-span-3 flex items-start gap-3 text-left">
                            <X className="w-4 h-4 shrink-0 mt-0.5 text-red-500/75" />
                            <span className={`text-[12px] font-semibold ${
                              theme === 'light' ? 'text-zinc-650' : 'text-zinc-400'
                            }`}>
                              {row.cloud}
                            </span>
                          </div>

                          {/* Middle Column: Versus Badge */}
                          <div className="md:col-span-1 flex items-center justify-center">
                            <span className={`text-[8px] font-bold uppercase tracking-wider py-1 px-2 rounded-full border leading-none select-none ${
                              theme === 'light'
                                ? 'bg-zinc-100 text-zinc-500 border-zinc-200/80'
                                : 'bg-white/[0.04] text-zinc-400 border-white/5'
                            }`}>
                              {row.feature}
                            </span>
                          </div>

                          {/* Right Column: Local-First Smart Imagify Way */}
                          <div className="md:col-span-3 flex items-start md:justify-end gap-3 text-left md:text-right">
                            <span className={`text-[12px] font-bold order-2 md:order-1 ${
                              theme === 'light' ? 'text-zinc-800' : 'text-white'
                            }`}>
                              {row.local}
                            </span>
                            <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#aace16] order-1 md:order-2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Technical Highlights Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                {[
                  { num: '90%', label: 'Compression Savings', sub: 'Average file size reduction' },
                  { num: '<1s', label: 'Processing Speed', sub: 'Per image via WASM engine' },
                  { num: '0', label: 'Server Uploads', sub: 'Everything stays on device' },
                  { num: '8+', label: 'Studio Tools', sub: 'All accessible for free' },
                ].map((item, idx) => (
                  <motion.div
                    key={'highlight-'+idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                    className={`group text-center p-5 rounded-2xl border transition-all duration-300 relative overflow-hidden select-none hover:-translate-y-1 ${
                      theme === 'light'
                        ? 'bg-gradient-to-b from-white via-zinc-50/70 to-zinc-100/50 border-zinc-200/80 hover:border-zinc-350 shadow-sm hover:shadow-md'
                        : 'bg-gradient-to-b from-[#141418] via-[#0b0b0e] to-[#040406] border-white/[0.08] hover:border-white/20 shadow-xl shadow-black/25'
                    }`}
                  >
                    {/* Top ambient line flare */}
                    <div className={`absolute top-0 inset-x-8 h-[1px] bg-gradient-to-r from-transparent ${
                      theme === 'light' ? 'via-black/15 group-hover:via-black/35' : 'via-white/30 group-hover:via-white/55'
                    } to-transparent pointer-events-none`} />

                    <div className={`text-3xl font-black font-mono tracking-tight transition-colors duration-300 ${
                      theme === 'light' ? 'text-zinc-900 group-hover:text-black' : 'text-[#aace16] group-hover:text-white'
                    }`}>{item.num}</div>
                    
                    <div className={`text-[10px] font-bold uppercase tracking-wider mt-2.5 transition-colors duration-300 ${
                      theme === 'light' ? 'text-zinc-700' : 'text-zinc-300'
                    }`}>{item.label}</div>
                    
                    <div className={`text-[9px] font-medium mt-1 transition-colors duration-300 ${
                      theme === 'light' ? 'text-zinc-450' : 'text-zinc-500'
                    }`}>{item.sub}</div>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Section B: How It Works */}{/* Section B: How It Works */}
            <section className="space-y-14">
              <div className="text-center max-w-3xl mx-auto space-y-4">
                <span className={`text-[10px] font-mono uppercase tracking-[0.25em] font-bold ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  How It Works
                </span>
                <h2 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] ${
                  theme === 'light' ? 'text-zinc-950' : 'text-white'
                }`}>
                  Three Steps <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>with absolutely</span><br />
                  <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>zero server waiting time</span>
                </h2>
                <p className={`text-sm md:text-base font-normal leading-relaxed max-w-xl mx-auto ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  No accounts needed. No files leave your device. Just drag, edit, and download.
                </p>
              </div>

              {/* Steps */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 relative">
                {/* Connecting gradient glow line (desktop only) */}
                <div className={`hidden md:block absolute top-10 left-[20%] right-[20%] h-[1px] ${
                  theme === 'light'
                    ? 'bg-gradient-to-r from-zinc-200/0 via-zinc-300 to-zinc-200/0'
                    : 'bg-gradient-to-r from-zinc-500/0 via-zinc-500/25 to-zinc-500/0'
                }`} />
                <div className={`hidden md:block absolute top-10 left-[20%] right-[20%] h-[3px] blur-sm ${
                  theme === 'light'
                    ? 'bg-gradient-to-r from-transparent via-zinc-300/50 to-transparent'
                    : 'bg-gradient-to-r from-transparent via-[#aace16]/10 to-transparent'
                }`} />
                
                {[
                  { num: '01', title: 'Drop Your Photos', desc: 'Drag your images into the studio — entire folders or individual files. They stay right on your device.' },
                  { num: '02', title: 'Customize & Edit', desc: 'Adjust quality, size, and format with simple sliders. Remove backgrounds, erase objects, or build collages.' },
                  { num: '03', title: 'Download Instantly', desc: 'Save your optimized files in one click. Everything is processed on your computer — nothing gets uploaded.' },
                ].map((step, idx) => (
                  <div key={`step-${idx}`} className={`flex flex-col items-center md:items-start text-center md:text-left space-y-4 relative p-5 rounded-2xl transition-all duration-300 ${
                    theme === 'light' ? 'hover:bg-zinc-50' : 'hover:bg-white/[0.02]'
                  }`}>
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl text-lg font-mono font-black relative z-10 transition-colors ${
                      theme === 'light'
                        ? 'bg-zinc-100 text-zinc-400 group-hover:text-zinc-600'
                        : 'bg-white/[0.05] text-zinc-500 border border-white/[0.06]'
                    }`}>
                      {step.num}
                    </div>
                    <h3 className={`text-[15px] font-bold tracking-tight ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>{step.title}</h3>
                    <p className={`text-[13px] leading-relaxed max-w-xs mx-auto md:mx-0 ${theme === 'light' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      {step.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Section C: Pricing Plans with Green Grid Effect */}
            <section id="saas-pricing-section" className="space-y-12 relative">
              {/* Green grid gradient background effect */}
              <div className="absolute -top-32 left-0 right-0 h-64 pointer-events-none overflow-hidden">
                <div className={`absolute inset-0 ${theme === 'light' ? 'opacity-[0.04]' : 'opacity-[0.06]'}`} style={{
                  backgroundImage: `linear-gradient(rgba(170,206,22,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(170,206,22,0.5) 1px, transparent 1px)`,
                  backgroundSize: '60px 60px'
                }} />
                <div className="absolute inset-0" style={{
                  background: theme === 'light'
                    ? 'linear-gradient(to bottom, transparent 0%, #f8f9fc 80%)'
                    : 'linear-gradient(to bottom, transparent 0%, #09090b 80%)'
                }} />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[200px] pointer-events-none" style={{
                  background: 'radial-gradient(ellipse at center, rgba(170,206,22,0.1) 0%, transparent 70%)'
                }} />
              </div>

              <div className="text-center max-w-3xl mx-auto space-y-5 relative z-10">
                <span className={`text-[10px] font-mono uppercase tracking-[0.25em] font-bold ${theme === 'light' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  Simple Pricing
                </span>
                <h2 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] ${
                  theme === 'light' ? 'text-zinc-950' : 'text-white'
                }`}>
                  Choose the Plan <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>that</span><br />
                  <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>fits your creative needs</span>
                </h2>
                
                {/* Billing toggle */}
                <div className="inline-flex items-center gap-3 p-1 rounded-full border border-slate-200 dark:border-white/5 bg-slate-100 dark:bg-zinc-950/60 text-[9px] font-bold uppercase tracking-widest select-none">
                  <button 
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-4 py-1.5 rounded-full transition-all cursor-pointer ${
                      billingPeriod === 'monthly'
                        ? theme === 'light' ? 'bg-white text-slate-900 shadow-sm font-black' : 'bg-zinc-800 text-white shadow-sm font-black'
                        : 'text-zinc-500'
                    }`}
                  >
                    Monthly
                  </button>
                  <button 
                    onClick={() => setBillingPeriod('yearly')}
                    className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 cursor-pointer ${
                      billingPeriod === 'yearly'
                        ? theme === 'light' ? 'bg-white text-slate-900 shadow-sm font-black' : 'bg-zinc-800 text-white shadow-sm font-black'
                        : 'text-zinc-500'
                    }`}
                  >
                    Yearly
                    <span className={`px-1.5 py-0.5 rounded text-[7px] font-black lowercase tracking-normal ${theme === 'light' ? 'bg-black text-white' : 'bg-white text-black'}`}>
                      -20%
                    </span>
                  </button>
                </div>
              </div>

              {/* Plans Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-4">
                {(() => {
                  const rawPackages = dbPackages.length > 0 ? dbPackages : [
                    {
                      slug: 'free',
                      name: 'Free',
                      price_monthly: 0,
                      price_yearly: 0,
                      features: [
                        "5MB max file size limit",
                        "Local WASM image optimizer",
                        "10 batch processes / execution",
                        "Canvas layout art board tools",
                        "Privacy-first offline processing"
                      ],
                      stripe_monthly_link: 'https://stripe.com/mock/free-monthly',
                      stripe_yearly_link: 'https://stripe.com/mock/free-yearly'
                    },
                    {
                      slug: 'starter',
                      name: 'Starter',
                      price_monthly: 19,
                      price_yearly: 190,
                      features: [
                        "25MB max file size limit",
                        "WASM local vectorizer engine",
                        "Unlimited local compressions",
                        "Basic AI background tracing",
                        "Standard email support access"
                      ],
                      stripe_monthly_link: 'https://stripe.com/mock/starter-monthly',
                      stripe_yearly_link: 'https://stripe.com/mock/starter-yearly'
                    },
                    {
                      slug: 'pro',
                      name: 'Pro',
                      price_monthly: 99,
                      price_yearly: 990,
                      features: [
                        "50MB max file size limit",
                        "HD GPU background removals",
                        "Parallel multi-threaded tasks",
                        "Signature high-speed processes",
                        "24/7 Priority developer support"
                      ],
                      stripe_monthly_link: 'https://stripe.com/mock/pro-monthly',
                      stripe_yearly_link: 'https://stripe.com/mock/pro-yearly'
                    },
                    {
                      slug: 'pro-team',
                      name: 'Pro Team',
                      price_monthly: 499,
                      price_yearly: 4990,
                      features: [
                        "100MB max file size limit",
                        "Team workspace pooling option",
                        "SSO directory integration keys",
                        "Shared credit & asset pipelines",
                        "Unlimited local GPU rendering"
                      ],
                      stripe_monthly_link: 'https://stripe.com/mock/proteam-monthly',
                      stripe_yearly_link: 'https://stripe.com/mock/proteam-yearly'
                    }
                  ];

                  return rawPackages.map((pkg) => {
                    const features = [...(pkg.features || [])];
                    while (features.length < 5) {
                      features.push(""); // Pad to exactly 5 elements
                    }

                    const monthlyPrice = Number(pkg.price_monthly);
                    const yearlyPrice = Number(pkg.price_yearly);
                    
                    // Calculated values
                    const displayPrice = billingPeriod === 'yearly' ? Math.floor(yearlyPrice / 12) : monthlyPrice;
                    
                    const getCurrencyDetails = (usdVal: number, currCode: string) => {
                      if (usdVal === 0) return { symbol: '$', val: 0 };
                      switch (currCode) {
                        case 'EUR':
                          return { symbol: '€', val: Math.round(usdVal * 0.92) };
                        case 'GBP':
                          return { symbol: '£', val: Math.round(usdVal * 0.79) };
                        case 'PKR':
                          return { symbol: 'Rs. ', val: Math.round(usdVal * 278) };
                        case 'USD':
                        default:
                          return { symbol: '$', val: usdVal };
                      }
                    };
                    const { symbol: currencySymbol, val: convertedPrice } = getCurrencyDetails(displayPrice, footerCurrency);
                    const isFeatured = pkg.slug === 'pro' || pkg.slug === 'plus' || pkg.slug === 'ultra';

                    const creditsMap: Record<string, string> = {
                      free: '50',
                      starter: '250',
                      pro: '1,500',
                      'pro-team': '10,000',
                      plus: '2,000',
                      ultra: '5,000',
                      business: '15,000'
                    };
                    const displayCredits = pkg.credits || creditsMap[pkg.slug] || '1,000';

                    const getSubtitle = (slug: string) => {
                      switch (slug) {
                        case 'free':
                          return 'For everyone starting out or testing our offline studio tools';
                        case 'starter':
                        case 'basic':
                          return 'For early-stage creators, solopreneurs and indie devs';
                        case 'pro':
                        case 'plus':
                        case 'ultra':
                          return 'For professional designers, developers and power creators';
                        case 'pro-team':
                        case 'business':
                          return 'For teams and agencies working on cool products together';
                        default:
                          return 'Premium access package';
                      }
                    };

                    const getUnderButtonDesc = (slug: string) => {
                      switch (slug) {
                        case 'free':
                          return 'Free forever. No registration required.';
                        case 'starter':
                        case 'basic':
                          return 'Lifetime access. Free updates. No recurring fees.';
                        case 'pro':
                        case 'plus':
                        case 'ultra':
                        case 'pro-team':
                        case 'business':
                          return 'Lifetime access. Free updates. No recurring fees.';
                        default:
                          return 'Lifetime access. Free updates. Cancel anytime.';
                      }
                    };

                    const getButtonText = (slug: string) => {
                      switch (slug) {
                        case 'free':
                          return 'Get started for free';
                        case 'starter':
                        case 'basic':
                          return 'Get Starter access';
                        case 'pro':
                        case 'plus':
                        case 'ultra':
                          return 'Get all-access';
                        case 'pro-team':
                        case 'business':
                          return 'Get all-access for your team';
                        default:
                          return 'Select Plan';
                      }
                    };

                    return (
                      <div 
                        key={pkg.slug} 
                        className={`group p-6 rounded-xl border flex flex-col justify-between min-h-[495px] transition-all duration-300 relative overflow-hidden bg-[var(--sidebar-bg)] ${
                          isFeatured
                            ? 'border-[#aace16]/45 bg-[var(--bg-3)]'
                            : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                        }`}
                      >
                        {/* Relative Content Container */}
                        <div className="relative z-10 flex flex-col justify-between flex-grow">
                          <div>
                            {/* Title and Subtitle */}
                            <div className="space-y-1.5 text-left">
                              <div className="flex items-center gap-1.5">
                                {pkg.slug === 'starter' || pkg.slug === 'basic' ? (
                                  <Home className="w-3.5 h-3.5 text-[var(--text-3)]" />
                                ) : pkg.slug === 'pro' || pkg.slug === 'plus' || pkg.slug === 'ultra' ? (
                                  <Sparkles className="w-3.5 h-3.5 text-[#aace16]" />
                                ) : pkg.slug === 'pro-team' || pkg.slug === 'business' ? (
                                  <Cpu className="w-3.5 h-3.5 text-[var(--text-3)]" />
                                ) : null}
                                <h3 className="text-xs font-black uppercase tracking-wider text-[var(--text-1)]">
                                  {pkg.name}
                                </h3>
                              </div>
                              <p className="text-[10px] font-semibold leading-relaxed text-[var(--text-3)]">
                                {getSubtitle(pkg.slug)}
                              </p>
                            </div>

                            {/* Price block */}
                            <div className="flex items-baseline gap-0.5 text-left mt-5">
                              <span className="text-xl font-bold font-sans self-start mt-0.5 text-[var(--text-1)]">{currencySymbol}</span>
                              <span className="text-5xl font-black tracking-tight font-mono text-[var(--text-1)]">{convertedPrice.toLocaleString()}</span>
                              <div className="flex flex-col text-left justify-center pl-1.5 leading-[1.1] select-none">
                                <span className="text-[9px] font-black uppercase tracking-wider text-[var(--text-1)]">
                                  {pkg.slug === 'free' ? 'free forever' : 'one-time payment'}
                                </span>
                                <span className="text-[7px] font-bold font-mono uppercase tracking-wider text-[var(--text-3)]">
                                  {pkg.slug === 'free' ? 'no registration' : 'plus local taxes'}
                                </span>
                              </div>
                            </div>

                            {/* Credits block */}
                            <div className="mt-4 p-3 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] flex items-center justify-between select-none">
                              <span className="text-[9px] font-black uppercase tracking-wider text-[var(--text-3)]">Monthly Credits</span>
                              <span className="text-lg font-black font-mono tracking-tight text-[var(--text-1)]">{displayCredits}</span>
                            </div>

                            {/* Plan Action Button */}
                            <button 
                              onClick={() => {
                                onPlanSelect(pkg.slug);
                              }}
                              className={`w-full py-2.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all mt-5 cursor-pointer border ${
                                isFeatured
                                  ? 'bg-[#aace16] hover:bg-[#bdef2b] text-black border-[#aace16]/20'
                                  : 'bg-[var(--bg-2)] hover:bg-[var(--bg-3)] border border-[var(--border)] text-[var(--text-1)]'
                              }`}
                            >
                              {getButtonText(pkg.slug)}
                            </button>

                            {/* Bottom description */}
                            <p className="text-[9px] font-semibold text-left mt-3.5 text-[var(--text-3)]">
                              {getUnderButtonDesc(pkg.slug)}
                            </p>
                          </div>

                          {/* Features container */}
                          <div className="mt-6 pt-6 border-t border-[var(--border)]">
                            <ul className="space-y-3 text-[11px] font-semibold text-left">
                              {features.map((feature: string, idx: number) => (
                                <li key={idx} className="flex items-start gap-2.5 min-h-[16px]">
                                  {feature ? (
                                    <>
                                      <svg className="w-3.5 h-3.5 text-[#aace16] shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="m9 12 2 2 4-4" />
                                      </svg>
                                      <span className="leading-tight text-[var(--text-2)]">{feature}</span>
                                    </>
                                  ) : (
                                    <div className="h-4" /> // Empty placeholder to keep identical spacing
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </section>

            {/* Section D: Trust & Numbers */}
            <section className="p-8 rounded-xl border border-[var(--border)] bg-[var(--sidebar-bg)] text-center max-w-4xl mx-auto">
              <div className="flex flex-col sm:flex-row items-center justify-around gap-6 divide-y sm:divide-y-0 sm:divide-x divide-[var(--border)]">
                
                <div className="space-y-2 py-4 sm:py-0 w-full">
                  <div className="text-2xl font-mono font-black flex items-center justify-center gap-0.5 text-[var(--text-1)]">
                    ★ 4.9 <span className="text-[10px] font-sans font-bold text-[var(--text-3)]">/ 5.0</span>
                  </div>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-2)]">
                    User Rating
                  </h4>
                  <p className="text-[11px] leading-relaxed text-[var(--text-3)]">
                    Based on 12,000+ happy user reviews
                  </p>
                </div>

                <div className="space-y-2 py-4 sm:py-0 w-full sm:pl-6">
                  <div className="text-xl font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 text-[var(--text-1)]">
                    <Shield className="w-5 h-5" /> Private
                  </div>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-2)]">
                    Your Files Stay On Your Device
                  </h4>
                  <p className="text-[11px] leading-relaxed text-[var(--text-3)]">
                    Nothing is ever uploaded or stored on any server
                  </p>
                </div>

                <div className="space-y-2 py-4 sm:py-0 w-full sm:pl-6">
                  <div className="text-xl font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 text-[var(--text-1)]">
                    <Zap className="w-5 h-5" /> Instant
                  </div>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-2)]">
                    Results in Under a Second
                  </h4>
                  <p className="text-[11px] leading-relaxed text-[var(--text-3)]">
                    No waiting, no queues — everything processes locally
                  </p>
                </div>
              </div>
            </section>

            {/* Section E: User Testimonials & Reviews Grid with Infinite Marquee */}
            <section className="space-y-12 overflow-hidden relative w-full">
              <style>{`
                @keyframes marquee-left {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                @keyframes marquee-right {
                  0% { transform: translateX(-50%); }
                  100% { transform: translateX(0); }
                }
                .animate-marquee-left {
                  display: flex;
                  width: max-content;
                  animation: marquee-left 40s linear infinite;
                  will-change: transform;
                }
                .animate-marquee-right {
                  display: flex;
                  width: max-content;
                  animation: marquee-right 40s linear infinite;
                  will-change: transform;
                }
                .animate-marquee-left:hover,
                .animate-marquee-right:hover {
                  animation-play-state: paused;
                }
              `}</style>

              <div className="text-center max-w-2xl mx-auto space-y-4 px-6">
                <span className={`text-[9px] font-mono uppercase tracking-[0.25em] font-black ${theme === 'light' ? 'text-slate-400' : 'text-zinc-500'}`}>
                  Reviews & Social Proof
                </span>
                <h2 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] ${
                  theme === 'light' ? 'text-zinc-950' : 'text-white'
                }`}>
                  Loved by Designers <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>working</span><br />
                  <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>across the entire planet</span>
                </h2>
                <p className={`text-xs md:text-sm font-semibold leading-relaxed max-w-lg mx-auto ${theme === 'light' ? 'text-slate-600' : 'text-zinc-400'}`}>
                  These are some real testimonials from developers and creators displaying the offline client studio suite in action.
                </p>
              </div>

              {/* Scrolling marquees wrapping */}
              <div className="relative space-y-6 pt-4 w-full select-none">
                
                {/* Horizontal side fading gradient masks */}
                <div className={`absolute inset-y-0 left-0 w-12 sm:w-32 bg-gradient-to-r z-20 pointer-events-none ${
                  theme === 'light' ? 'from-[#f8f9fc] to-transparent' : 'from-[#09090b] to-transparent'
                }`} />
                <div className={`absolute inset-y-0 right-0 w-12 sm:w-32 bg-gradient-to-l z-20 pointer-events-none ${
                  theme === 'light' ? 'from-[#f8f9fc] to-transparent' : 'from-[#09090b] to-transparent'
                }`} />

                {/* Row 1: Leftward marquee */}
                <div className="flex overflow-x-hidden w-full">
                  <div className="animate-marquee-left gap-5 px-2">
                    {[
                      {
                        name: "Darius Flynn",
                        username: "@flynnn",
                        avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Running bulk image compressor locally via WASM is a game changer for <span class='text-[#aace16] font-bold'>@smartimagify</span>. No upload delay, no server egress fees. It's absolute magic."
                      },
                      {
                        name: "Felix B.",
                        username: "@felixbs",
                        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Digging into <span class='text-[#aace16] font-bold'>@smartimagify</span>. Those GPU-accelerated background removals and edge mask details are giving me serious design envy."
                      },
                      {
                        name: "Fabrizio Fernandez",
                        username: "@fab3304",
                        avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Playing around with <span class='text-[#aace16] font-bold'>@smartimagify</span> suddenly made me feel inspired to launch my side project. Extremely fast client-side execution."
                      },
                      {
                        name: "Kai Nakamura",
                        username: "@KaiNakWaves",
                        avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Just optimized my entire photography directory with <span class='text-[#aace16] font-bold'>@smartimagify</span>. Its client-side vectorizer is speaking my language. No drama, just speed."
                      },
                      {
                        name: "Olivia Blackwood",
                        username: "@olivia1992",
                        avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "<span class='text-[#aace16] font-bold'>@smartimagify</span> is not messing around with its local image studio tab. Absolute privacy since files never leave my device."
                      },
                      {
                        name: "Esme Rothschild",
                        username: "@EsmeRothArt",
                        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "<span class='text-[#aace16] font-bold'>@smartimagify</span>'s collage editor is slick. That canvas scaling makes me feel like I'm building templates in a desktop app."
                      }
                    ].map((item, idx) => (
                      <div 
                        key={`r1-orig-${idx}`} 
                        className="p-5 rounded-lg border border-[var(--border)] bg-[var(--sidebar-bg)] hover:border-[var(--border-hover)] shrink-0 text-left transition-all duration-300 w-[300px] flex flex-col justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <img 
                            src={item.avatar} 
                            alt={item.name} 
                            className="w-9 h-9 rounded-full object-cover border border-[var(--border)] grayscale"
                          />
                          <div>
                            <h4 className="text-[11px] font-black uppercase tracking-wider text-[var(--text-1)]">
                              {item.name}
                            </h4>
                            <p className="text-[9px] font-bold font-mono text-[var(--text-3)]">
                              {item.username}
                            </p>
                          </div>
                        </div>
                        <p 
                          className="text-[11px] font-medium leading-relaxed mt-3.5 text-[var(--text-2)]"
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                      </div>
                    ))}
                    {[
                      {
                        name: "Darius Flynn",
                        username: "@flynnn",
                        avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Running bulk image compressor locally via WASM is a game changer for <span class='text-[#aace16] font-bold'>@smartimagify</span>. No upload delay, no server egress fees. It's absolute magic."
                      },
                      {
                        name: "Felix B.",
                        username: "@felixbs",
                        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Digging into <span class='text-[#aace16] font-bold'>@smartimagify</span>. Those GPU-accelerated background removals and edge mask details are giving me serious design envy."
                      },
                      {
                        name: "Fabrizio Fernandez",
                        username: "@fab3304",
                        avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Playing around with <span class='text-[#aace16] font-bold'>@smartimagify</span> suddenly made me feel inspired to launch my side project. Extremely fast client-side execution."
                      },
                      {
                        name: "Kai Nakamura",
                        username: "@KaiNakWaves",
                        avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Just optimized my entire photography directory with <span class='text-[#aace16] font-bold'>@smartimagify</span>. Its client-side vectorizer is speaking my language. No drama, just speed."
                      },
                      {
                        name: "Olivia Blackwood",
                        username: "@olivia1992",
                        avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "<span class='text-[#aace16] font-bold'>@smartimagify</span> is not messing around with its local image studio tab. Absolute privacy since files never leave my device."
                      },
                      {
                        name: "Esme Rothschild",
                        username: "@EsmeRothArt",
                        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "<span class='text-[#aace16] font-bold'>@smartimagify</span>'s collage editor is slick. That canvas scaling makes me feel like I'm building templates in a desktop app."
                      }
                    ].map((item, idx) => (
                      <div 
                        key={`r1-dup-${idx}`} 
                        className="p-5 rounded-lg border border-[var(--border)] bg-[var(--sidebar-bg)] hover:border-[var(--border-hover)] shrink-0 text-left transition-all duration-300 w-[300px] flex flex-col justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <img 
                            src={item.avatar} 
                            alt={item.name} 
                            className="w-9 h-9 rounded-full object-cover border border-[var(--border)] grayscale"
                          />
                          <div>
                            <h4 className="text-[11px] font-black uppercase tracking-wider text-[var(--text-1)]">
                              {item.name}
                            </h4>
                            <p className="text-[9px] font-bold font-mono text-[var(--text-3)]">
                              {item.username}
                            </p>
                          </div>
                        </div>
                        <p 
                          className="text-[11px] font-medium leading-relaxed mt-3.5 text-[var(--text-2)]"
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 2: Rightward marquee */}
                <div className="flex overflow-x-hidden w-full">
                  <div className="animate-marquee-right gap-5 px-2">
                    {[
                      {
                        name: "Marcus Vance",
                        username: "@marcusv_dev",
                        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Swapping between formats (PNG to AVIF) locally with zero latency is just crazy. <span class='text-[#aace16] font-bold'>@smartimagify</span> saves me hours of pipeline scripts."
                      },
                      {
                        name: "Clara Dubois",
                        username: "@clara_d",
                        avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Privacy-first AI tools that actually run 100% inside the browser? <span class='text-[#aace16] font-bold'>@smartimagify</span> is officially in my daily developer bookmark bar."
                      },
                      {
                        name: "Aris Thorne",
                        username: "@aris_t",
                        avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Offline bulk optimization is a superpower. I processed 150 product shots in seconds using <span class='text-[#aace16] font-bold'>@smartimagify</span>."
                      },
                      {
                        name: "Sienna Martinez",
                        username: "@sienna_m",
                        avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "No more subscription bloat for basic image editing. <span class='text-[#aace16] font-bold'>@smartimagify</span> gives me Photoshop-level utilities in a clean browser window."
                      },
                      {
                        name: "Elena Rostova",
                        username: "@elena_design",
                        avatar: "https://images.unsplash.com/photo-1554151228-14d9def656e4?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "The speed of the background masking on complex textures is unreal. Kudos to the creators of <span class='text-[#aace16] font-bold'>@smartimagify</span>."
                      },
                      {
                        name: "Devon Cole",
                        username: "@devon_c",
                        avatar: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "I was skeptical about client-side AI upscaling, but the output is razor sharp. Incredible work on <span class='text-[#aace16] font-bold'>@smartimagify</span>!"
                      }
                    ].map((item, idx) => (
                      <div 
                        key={`r2-orig-${idx}`} 
                        className="p-5 rounded-lg border border-[var(--border)] bg-[var(--sidebar-bg)] hover:border-[var(--border-hover)] shrink-0 text-left transition-all duration-300 w-[300px] flex flex-col justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <img 
                            src={item.avatar} 
                            alt={item.name} 
                            className="w-9 h-9 rounded-full object-cover border border-[var(--border)] grayscale"
                          />
                          <div>
                            <h4 className="text-[11px] font-black uppercase tracking-wider text-[var(--text-1)]">
                              {item.name}
                            </h4>
                            <p className="text-[9px] font-bold font-mono text-[var(--text-3)]">
                              {item.username}
                            </p>
                          </div>
                        </div>
                        <p 
                          className="text-[11px] font-medium leading-relaxed mt-3.5 text-[var(--text-2)]"
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                      </div>
                    ))}
                    {[
                      {
                        name: "Marcus Vance",
                        username: "@marcusv_dev",
                        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Swapping between formats (PNG to AVIF) locally with zero latency is just crazy. <span class='text-[#aace16] font-bold'>@smartimagify</span> saves me hours of pipeline scripts."
                      },
                      {
                        name: "Clara Dubois",
                        username: "@clara_d",
                        avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Privacy-first AI tools that actually run 100% inside the browser? <span class='text-[#aace16] font-bold'>@smartimagify</span> is officially in my daily developer bookmark bar."
                      },
                      {
                        name: "Aris Thorne",
                        username: "@aris_t",
                        avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "Offline bulk optimization is a superpower. I processed 150 product shots in seconds using <span class='text-[#aace16] font-bold'>@smartimagify</span>."
                      },
                      {
                        name: "Sienna Martinez",
                        username: "@sienna_m",
                        avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "No more subscription bloat for basic image editing. <span class='text-[#aace16] font-bold'>@smartimagify</span> gives me Photoshop-level utilities in a clean browser window."
                      },
                      {
                        name: "Elena Rostova",
                        username: "@elena_design",
                        avatar: "https://images.unsplash.com/photo-1554151228-14d9def656e4?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "The speed of the background masking on complex textures is unreal. Kudos to the creators of <span class='text-[#aace16] font-bold'>@smartimagify</span>."
                      },
                      {
                        name: "Devon Cole",
                        username: "@devon_c",
                        avatar: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=150&h=150&q=80",
                        text: "I was skeptical about client-side AI upscaling, but the output is razor sharp. Incredible work on <span class='text-[#aace16] font-bold'>@smartimagify</span>!"
                      }
                    ].map((item, idx) => (
                      <div 
                        key={`r2-dup-${idx}`} 
                        className="p-5 rounded-lg border border-[var(--border)] bg-[var(--sidebar-bg)] hover:border-[var(--border-hover)] shrink-0 text-left transition-all duration-300 w-[300px] flex flex-col justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <img 
                            src={item.avatar} 
                            alt={item.name} 
                            className="w-9 h-9 rounded-full object-cover border border-[var(--border)] grayscale"
                          />
                          <div>
                            <h4 className="text-[11px] font-black uppercase tracking-wider text-[var(--text-1)]">
                              {item.name}
                            </h4>
                            <p className="text-[9px] font-bold font-mono text-[var(--text-3)]">
                              {item.username}
                            </p>
                          </div>
                        </div>
                        <p 
                          className="text-[11px] font-medium leading-relaxed mt-3.5 text-[var(--text-2)]"
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </section>

            {/* Section F: Comprehensive FAQs Accordion (Double-Column Balanced Layout) */}
            <section className={`w-full py-12 transition-all duration-300 ${
              theme === 'light' ? 'bg-white' : 'bg-black'
            }`}>
              <div className="max-w-[1260px] mx-auto px-6 md:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                  
                  {/* Left Column: Sticky Header & Description (50% width) */}
                  <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-32 space-y-4 text-left">
                      
                      {/* Subtitle Pill matching screenshot */}
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-[var(--bg-3)] border border-[var(--border)] rounded-full text-[10px] font-bold tracking-wider text-[var(--text-1)] uppercase select-none">
                        <span className="w-4 h-4 rounded-full bg-[#aace16] text-black flex items-center justify-center font-black text-[9px] leading-none">
                          ?
                        </span>
                        SUPPORT GUIDE
                      </div>

                      {/* Header Typography matching screenshot */}
                      <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.15] text-[var(--text-1)]">
                        Helpful Answers <span className="font-light text-[var(--text-3)]">to</span><br />
                        <span className="font-light text-[var(--text-3)]">Your Common Questions</span>
                      </h2>

                      {/* Category Navigation with line indicators */}
                      <div className="space-y-1.5 pt-1">
                        {['General & Support', 'Compression & Engine', 'Privacy & Security', 'Plans & Billing'].map((cat) => {
                          const isActive = activeLandingCat === cat;
                          return (
                            <button
                              key={cat}
                              onClick={() => {
                                setActiveLandingCat(cat);
                                setActiveFaq(null); // Reset open accordion item when changing category
                              }}
                              className={`w-full text-left py-2 text-xs sm:text-sm font-semibold tracking-wide transition-all duration-300 flex items-center gap-2.5 select-none cursor-pointer group ${
                                isActive
                                  ? 'text-[var(--text-1)] font-bold'
                                  : 'text-[var(--text-3)] hover:text-[var(--text-1)]'
                              }`}
                            >
                              {isActive && (
                                <span className="w-4 h-[2px] transition-all duration-300 bg-[#aace16]" />
                              )}
                              <span>{cat}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* All FAQs green button */}
                      <div className="pt-1">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            window.history.pushState(null, '', '/faq');
                            window.dispatchEvent(new PopStateEvent('popstate'));
                          }}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--bg-2)] hover:bg-[var(--bg-3)] border border-[var(--border)] text-[var(--text-1)] rounded-full text-xs font-bold tracking-wide transition-all duration-300 select-none cursor-pointer shadow-sm hover:scale-[1.02] active:scale-95"
                        >
                          <HelpCircle className="w-4 h-4" />
                          <span>All FAQs</span>
                        </button>
                      </div>

                    </div>
                  </div>

                  {/* Right Column: Premium Accordion List (50% width) */}
                  <div className="lg:col-span-7">
                    <div className="space-y-4">
                      {landingFaqs
                        .filter(f => f.category === activeLandingCat)
                        .map((item, idx) => {
                          const isOpen = activeFaq === idx;
                          return (
                            <div 
                              key={idx} 
                              className={`p-6 rounded-lg border transition-all duration-300 relative overflow-hidden bg-[var(--sidebar-bg)] ${
                                isOpen
                                  ? 'border-[#aace16]/45 bg-[var(--bg-3)]'
                                  : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                              }`}
                            >
                              <button 
                                onClick={() => setActiveFaq(isOpen ? null : idx)}
                                className={`w-full text-left flex items-center justify-between gap-4 font-semibold text-xs sm:text-sm select-none cursor-pointer transition-colors duration-300 ${
                                  isOpen 
                                    ? 'text-[var(--text-1)] font-bold'
                                    : 'text-[var(--text-2)] hover:text-[var(--text-1)]'
                                }`}
                              >
                                <span>{item.question}</span>
                                <div className="text-lg font-normal leading-none select-none">
                                  {isOpen ? '−' : '+'}
                                </div>
                              </button>
                              
                              <div 
                                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                                  isOpen ? 'max-h-[220px] opacity-100 mt-4' : 'max-h-0 opacity-0 pointer-events-none'
                                }`}
                              >
                                <p className="text-[12px] sm:text-[13px] leading-relaxed text-left text-[var(--text-2)]">
                                  {item.answer}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>
      )}

      {!specialContent && (
        <>
          {/* CTA Banner Section — Full Width with Black and White Workspace Background */}
          <section className={`relative w-full py-24 md:py-32 overflow-hidden z-10 border-t transition-colors duration-300 ${
            theme === 'light'
              ? 'bg-zinc-50 border-zinc-200 text-zinc-900'
              : 'bg-[#030303] border-white/[0.06] text-white'
          }`}>
            
            {/* Background Image - Full Width, Black and White, High-Contrast */}
            <div className="absolute inset-0 bg-cover bg-center pointer-events-none select-none opacity-30 dark:opacity-45 z-0" style={{
              backgroundImage: "url('/designer_workspace_bw.png')"
            }} />
            
            {/* Overlay Gradient for Text Contrast */}
            <div className="absolute inset-0 pointer-events-none z-0 bg-gradient-to-b from-[var(--bg)]/95 via-[var(--bg)]/80 to-[var(--bg)]/95" />

            {/* Ambient top glowing line */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#aace16]/40 to-transparent z-10" />

            <div className="max-w-4xl mx-auto px-6 relative z-10 flex flex-col items-center justify-center gap-6 text-center">
              
              {/* Kicker badge */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#aace16]/10 border border-[#aace16]/25 text-[#aace16] text-[9px] font-black uppercase tracking-widest relative z-10">
                <span className="w-1.5 h-1.5 rounded-full bg-[#aace16] animate-pulse" />
                Offline Engine
              </div>
              
              {/* Heading */}
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] text-[var(--text-1)]">
                Ready to Begin <span className="font-light text-[var(--text-3)]">transforming and</span><br />
                <span className="font-light text-[var(--text-3)]">optimizing your images?</span>
              </h2>

              {/* Subtext - simplified and shorter */}
              <p className="text-xs md:text-sm font-semibold leading-relaxed max-w-xl text-[var(--text-2)]">
                Start editing instantly. No sign-up required. Completely private, client-side, right in your browser.
              </p>

              {/* Button */}
              <div className="relative group shrink-0 w-full sm:w-auto mt-4">
                <button 
                  onClick={onLaunchTool}
                  className="relative w-full sm:w-auto px-10 py-4 bg-[var(--text-1)] text-[var(--bg)] hover:opacity-90 text-[11px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 cursor-pointer hover:scale-[1.03] active:scale-95 flex items-center justify-center gap-2 border border-[var(--border)] overflow-hidden group"
                >
                  <span>Launch Studio</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>

            </div>
          </section>
        </>
      )}

      <footer className="relative pt-28 pb-16 overflow-hidden z-10 border-t border-[var(--border)] bg-[var(--sidebar-bg)] text-[var(--text-1)]">
        {/* Decorative Grid Mesh Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px] pointer-events-none opacity-40 z-0" />
        
        {/* Dotted World Map Background Overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden select-none opacity-[0.08] z-0">
          <img 
            src="/dotted-map.svg" 
            alt="Dotted World Map Grid" 
            className="w-full h-full object-cover object-center scale-[1.08] dark:invert"
          />
        </div>

        {/* Top visual separator with dynamic neon color */}
        <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#aace16]/40 to-transparent" />

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-16">
          
          {/* Top Row: Newsletter Banner & Live Statistics */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch pb-12 border-b border-[var(--border)]">
            
             {/* Newsletter widget (7 cols) */}
             <div className="lg:col-span-7 flex flex-col justify-center items-center lg:items-start text-center lg:text-left space-y-4">
               <div className="space-y-2 flex flex-col items-center lg:items-start">
                 <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#aace16]/10 text-[#aace16] text-[9px] font-black uppercase tracking-widest">
                   <span className="w-1.5 h-1.5 rounded-full bg-[#aace16] animate-ping" />
                   STAY UPDATED
                 </div>
                 <h3 className="text-xl font-extrabold premium-font-heading text-[var(--text-1)]">
                   Join the Private Studio Release Loop
                 </h3>
                 <p className="text-xs md:text-sm font-medium text-[var(--text-2)]">
                   Subscribe to receive offline tool enhancements, local security bulletins, and performance optimizations.
                 </p>
               </div>
 
               <div className="max-w-md w-full mx-auto lg:mx-0">
                 {!footerSubscribed ? (
                   <form onSubmit={handleFooterSubscribe} className="flex flex-col sm:flex-row gap-2.5 w-full">
                     <input 
                       type="email"
                       required
                       value={footerEmail}
                       onChange={(e) => setFooterEmail(e.target.value)}
                       placeholder="Enter your security-conscious email"
                       className="w-full sm:flex-1 px-4 py-2.5 text-xs font-semibold rounded-lg border border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-1)] placeholder-[var(--text-3)] outline-none transition-all duration-300 focus:border-[#aace16]/80"
                     />
                     <button
                       type="submit"
                       disabled={footerSubmitting}
                       className="w-full sm:w-auto px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all duration-300 active:scale-98 flex items-center justify-center gap-2 cursor-pointer bg-[var(--text-1)] hover:opacity-90 disabled:opacity-55 text-[var(--bg)] whitespace-nowrap"
                     >
                       {footerSubmitting ? (
                         <div className="w-4 h-4 rounded-full border border-current border-t-transparent animate-spin" />
                       ) : (
                         <>
                           <span>Subscribe</span>
                           <ArrowRight className="w-3.5 h-3.5" />
                         </>
                       )}
                     </button>
                   </form>
                 ) : (
                   <div className="flex items-center gap-3 py-2.5 px-4 rounded-lg bg-[#aace16]/10 border border-[#aace16]/20 text-[#aace16] w-fit mx-auto lg:mx-0">
                     <svg className="w-4 h-4 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                       <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                     </svg>
                     <span className="text-xs font-bold uppercase tracking-wider">Subscribed Successfully!</span>
                   </div>
                 )}
               </div>
             </div>

            {/* Live Statistics widget (5 cols) */}
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className="p-6 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] hover:border-[var(--border-hover)] transition-all duration-500">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5 text-left border-r border-[var(--border)] pr-4">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold block text-[var(--text-3)]">
                      Global Optimizations
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]"></span>
                      </span>
                      <span className="text-sm font-mono font-bold tracking-wider text-[var(--text-1)]">
                        {imageCounter.toLocaleString()}
                      </span>
                    </div>
                    <span className="text-[9px] block font-medium text-[var(--text-3)]">
                      locally processed images
                    </span>
                  </div>

                  <div className="space-y-2 text-left flex flex-col justify-center pl-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold block text-[var(--text-3)]">
                      Browser Compilers
                    </span>
                    <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-emerald-500 bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-xl w-fit transition-all duration-350 hover:border-emerald-500/40">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      WASM & WebGPU Active
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Middle Row: Links Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 text-left pt-4">
            
            {/* Column 1: Brand Info (3 cols) */}
            <div className="md:col-span-3 space-y-6 flex flex-col justify-between">
              <div className="space-y-4">
                <div 
                  className="inline-flex items-center gap-2.5 select-none cursor-pointer group" 
                  onClick={() => triggerRoute('/')}
                >
                  <img 
                    src={theme === 'light' ? "/Logo/logo white mode.png" : "/Logo/logo dark mode.png"} 
                    alt="Smart Imagify Logo" 
                    width="24"
                    height="24"
                    className="w-6 h-6 object-contain transition-all duration-500 transform group-hover:rotate-12 group-hover:scale-110 group-hover:brightness-110 filter drop-shadow-sm group-hover:drop-shadow-[0_0_8px_rgba(170,206,22,0.4)]"
                  />
                  <span className={`text-xl font-bold tracking-tight premium-font-heading transition-colors duration-300 ${theme === 'light' ? 'text-zinc-900 group-hover:text-[#aace16]' : 'text-white group-hover:text-[#aace16]'}`}>
                    Smart Imagify
                  </span>
                </div>
                
                <p className="text-xs md:text-sm leading-relaxed font-medium text-[var(--text-2)]">
                  Your offline, secure browser-based image studio. Fully private client-side processing workspace. No tracking or remote servers.
                </p>

                {/* Social Icons */}
                <div className="flex items-center gap-2.5 pt-2">
                  {[
                    { 
                      title: 'Discord', 
                      path: 'M18 8a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8z M10 12h.01 M14 12h.01 M6 8c2.5 1.5 5.5 1.5 8 0',
                      color: 'hover:bg-[#5865F2] hover:text-white hover:border-[#5865F2]'
                    },
                    { 
                      title: 'Twitter', 
                      path: 'M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z',
                      color: 'hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2]'
                    },
                    { 
                      title: 'GitHub', 
                      path: 'M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4 M9 18c-4.51 2-5-2-7-2',
                      color: 'hover:bg-zinc-900 hover:text-white hover:border-zinc-900 dark:hover:bg-white dark:hover:text-black dark:hover:border-white'
                    },
                  ].map((social, idx) => (
                    <a 
                      key={`social-${idx}`} 
                      href="#" 
                      className={`w-8.5 h-8.5 rounded-lg border border-[var(--border)] text-[var(--text-3)] bg-[var(--bg-3)] hover:text-[var(--text-1)] flex items-center justify-center transition-all duration-300 transform hover:-translate-y-0.5 ${social.color}`}
                      title={social.title}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d={social.path} />
                      </svg>
                    </a>
                  ))}
                  
                  <a 
                    href="mailto:support@smartimagify.com" 
                    className="w-8.5 h-8.5 rounded-lg border border-[var(--border)] text-[var(--text-3)] bg-[var(--bg-3)] flex items-center justify-center transition-all duration-300 transform hover:-translate-y-0.5 hover:bg-[#aace16] hover:text-[#090b11] hover:border-[#aace16]"
                    title="Email Support"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Horizontal Trust Badges */}
              <div className="flex flex-wrap gap-2 pt-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-3)] text-[8px] font-bold uppercase tracking-wider w-fit">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" />
                  GDPR Compliant
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-3)] text-[8px] font-bold uppercase tracking-wider w-fit">
                  <Lock className="w-3.5 h-3.5 text-[#aace16]" />
                  Zero Server Uploads
                </div>
              </div>
            </div>

            {/* Column 2: Products (3 cols) */}
            <div className="md:col-span-3 space-y-5">
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--text-3)]">
                Offline Tools
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'Image Compressor', key: 'compress' },
                  { label: 'Background Eraser', key: 'ai' },
                  { label: 'Collage Maker', key: 'collage' },
                  { label: 'Bulk Optimizer', key: 'bulk' },
                  { label: 'Passport Photo Studio', key: 'passport' }
                ].map((item, idx) => (
                  <li key={`prod-${idx}`}>
                    <a 
                      onClick={() => {
                        try { localStorage.setItem('si_initial_tab', item.key); } catch {}
                        onLaunchTool();
                      }} 
                      className="group flex items-center gap-1.5 transition-all duration-300 font-semibold text-xs cursor-pointer text-[var(--text-2)] hover:text-[var(--text-1)]"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#aace16] scale-0 group-hover:scale-100 transition-all duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Resources (3 cols) */}
            <div className="md:col-span-3 space-y-5">
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--text-3)]">
                Resources
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'Pricing Plans', path: '/pricing' },
                  { label: 'FAQs & Help', path: '/faq' },
                  { label: 'Privacy Center', path: '/privacy-policy' },
                  { label: 'Disclaimer Details', path: '/disclaimer' }
                ].map((item, idx) => (
                  <li key={`res-${idx}`}>
                    <a 
                      href={item.path} 
                      onClick={(e) => handleNavClick(e, item.path)} 
                      className="group flex items-center gap-1.5 transition-all duration-300 font-semibold text-xs text-[var(--text-2)] hover:text-[var(--text-1)]"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#aace16] scale-0 group-hover:scale-100 transition-all duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Legal & Security (3 cols) */}
            <div className="md:col-span-3 space-y-5">
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--text-3)]">
                Legal & Security
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'Privacy Policy', path: '/privacy-policy' },
                  { label: 'Terms of Service', path: '/terms-of-service' },
                  { label: 'Cookie Policy', path: '/cookie-policy' },
                  { label: 'Disclaimer Details', path: '/disclaimer' }
                ].map((item, idx) => (
                  <li key={`leg-${idx}`}>
                    <a 
                      href={item.path} 
                      onClick={(e) => handleNavClick(e, item.path)} 
                      className="group flex items-center gap-1.5 transition-all duration-300 font-semibold text-xs text-[var(--text-2)] hover:text-[var(--text-1)]"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#aace16] scale-0 group-hover:scale-100 transition-all duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* Copyright & Interactive Bottom Bar */}
          <div className={`border-t pt-8 flex flex-col md:flex-row items-center justify-between gap-6 ${
            theme === 'light' ? 'border-zinc-200/60' : 'border-white/[0.06]'
          }`}>
            
            {/* Copyright Info - Single Line */}
            <div className="text-center md:text-left">
              <p className="text-xs font-semibold text-[var(--text-3)]">
                © 2026 Smart Imagify. All rights reserved. <span className="mx-2 opacity-30">|</span> Developed by <a href="https://www.ayshdan.com" target="_blank" rel="noopener noreferrer" className="hover:underline font-extrabold text-[var(--text-2)] hover:text-[#aace16] transition-colors">Ayshdan Innovations</a>.
              </p>
            </div>

            {/* Interactive Currency & Language Selectors */}
            <div className="flex items-center gap-3">
              
              {/* Language Selector Dropdown */}
              <div ref={langRef} className="relative select-none">
                <button
                  type="button"
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className={`flex items-center justify-between text-[10px] font-extrabold tracking-widest uppercase pl-3 pr-8 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-2)] hover:text-[var(--text-1)] focus:outline-none cursor-pointer transition-all duration-300 gap-1.5 min-w-[90px] select-none ${langDropdownOpen ? 'border-[#aace16]/80 text-[#aace16]' : ''}`}
                >
                  <span>{languages.find(l => l.code === footerLanguage)?.label || '🇺🇸 EN'}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-455 transition-transform duration-300 ${langDropdownOpen ? 'rotate-180 text-[#aace16]' : ''}`} />
                </button>
                {langDropdownOpen && (
                  <div className="absolute bottom-full mb-2 right-0 w-32 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--sidebar-bg)] shadow-xl z-50 transition-all duration-200 origin-bottom-right">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          setFooterLanguage(lang.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-extrabold tracking-wider text-left transition-colors duration-200 cursor-pointer ${
                          footerLanguage === lang.code
                            ? 'bg-[#aace16]/10 text-[#aace16]'
                            : 'hover:bg-[var(--bg-3)] text-[var(--text-2)] hover:text-[var(--text-1)]'
                        }`}
                      >
                        <span>{lang.label}</span>
                        {footerLanguage === lang.code && (
                          <div className="w-1.5 h-1.5 rounded-full bg-[#aace16]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Currency Selector Dropdown */}
              <div ref={currRef} className="relative select-none">
                <button
                  type="button"
                  onClick={() => setCurrDropdownOpen(!currDropdownOpen)}
                  className={`flex items-center justify-between text-[10px] font-extrabold tracking-widest uppercase pl-3 pr-8 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-2)] hover:text-[var(--text-1)] focus:outline-none cursor-pointer transition-all duration-300 gap-1.5 min-w-[100px] select-none ${currDropdownOpen ? 'border-[#aace16]/80 text-[#aace16]' : ''}`}
                >
                  <span>{currencies.find(c => c.code === footerCurrency)?.label || 'USD ($)'}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-455 transition-transform duration-300 ${currDropdownOpen ? 'rotate-180 text-[#aace16]' : ''}`} />
                </button>
                {currDropdownOpen && (
                  <div className="absolute bottom-full mb-2 right-0 w-36 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--sidebar-bg)] shadow-xl z-50 transition-all duration-200 origin-bottom-right">
                    {currencies.map((curr) => (
                      <button
                        key={curr.code}
                        type="button"
                        onClick={() => {
                          setFooterCurrency(curr.code);
                          setCurrDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-extrabold tracking-wider text-left transition-colors duration-200 cursor-pointer ${
                          footerCurrency === curr.code
                            ? 'bg-[#aace16]/10 text-[#aace16]'
                            : 'hover:bg-[var(--bg-3)] text-[var(--text-2)] hover:text-[var(--text-1)]'
                        }`}
                      >
                        <span>{curr.label}</span>
                        {footerCurrency === curr.code && (
                          <div className="w-1.5 h-1.5 rounded-full bg-[#aace16]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      </footer>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg border border-[var(--border)] bg-[var(--bg-2)] hover:bg-[var(--bg-3)] text-[var(--text-1)] transition-all duration-300 transform translate-y-0 active:scale-95 cursor-pointer hover:-translate-y-1"
            title="Back to Top"
            aria-label="Back to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        )}


      {/* Interactive Water Bubble Pop Reward Modal */}
      {activeReward && (
        <div className="fixed inset-0 bg-[#040406]/75 backdrop-blur-md flex items-center justify-center z-[99999] p-4 font-sans animate-fade-in animate-in fade-in duration-200">
          <div className="w-full max-w-sm bg-[var(--sidebar-bg)] border border-[var(--border)] rounded-lg p-6 text-center space-y-6 shadow-2xl relative overflow-hidden">
            {/* Ambient glow in modal */}
            <div className="absolute -top-12 -left-12 w-24 h-24 rounded-full bg-[#aace16]/10 blur-xl pointer-events-none" />
            
            <div className="w-14 h-14 rounded-full bg-[#aace16]/10 border border-[#aace16]/20 text-[#aace16] flex items-center justify-center mx-auto animate-bounce">
              <Sparkles className="w-6 h-6 animate-pulse" />
            </div>

            <div className="space-y-2">
              <h3 className="text-base font-bold text-[var(--text-1)] premium-font-heading">
                {activeReward.type === 'discount' ? '🎉 10% Discount Unlocked!' : '⚡ +10 Free Credits Found!'}
              </h3>
              <p className="text-[var(--text-2)] text-[11px] leading-relaxed premium-font-body">
                {activeReward.type === 'discount' 
                  ? 'You successfully smashed the secret discount bubble! Claim it now to use on any premium tier.'
                  : 'You popped a credit bubble! Claim it now to add +10 image optimization credits to your account.'}
              </p>
            </div>

            {/* If logged in */}
            {userProfile ? (
              <div className="space-y-4">
                {activeReward.type === 'discount' ? (
                  <div className="bg-[var(--bg-3)] border border-[var(--border)] rounded-lg p-3 flex items-center justify-between">
                    <span className="text-xs font-mono tracking-widest text-[#aace16] font-bold">IMAGIFY10</span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText('IMAGIFY10');
                        alert('Discount code IMAGIFY10 copied to clipboard!');
                      }}
                      className="px-3 py-1 bg-[#aace16] hover:bg-[#bce01e] text-[#090b11] rounded-lg text-[10px] font-bold uppercase transition-all hover:scale-105 cursor-pointer"
                    >
                      Copy
                    </button>
                  </div>
                ) : (
                  <div className="bg-[#aace16]/10 border border-[#aace16]/20 rounded-lg p-3 text-[11px] text-[#aace16] font-semibold">
                    🎉 +10 Credits have been added to your profile!
                  </div>
                )}
                
                <button
                  onClick={() => setActiveReward(null)}
                  className="w-full py-2 bg-[var(--bg-3)] hover:bg-[var(--border)] text-[var(--text-2)] border border-[var(--border)] rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer"
                >
                  Awesome!
                </button>
              </div>
            ) : (
              /* If NOT logged in */
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setActiveReward(null);
                    onSignIn('/dashboard', 'signup');
                  }}
                  className="w-full py-2 bg-[#aace16] hover:bg-[#bce01e] text-[#090b11] rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all hover:shadow-[0_0_15px_rgba(170,206,22,0.3)] cursor-pointer"
                >
                  Claim Reward (Login / Sign Up)
                </button>
                <button
                  onClick={() => setActiveReward(null)}
                  className="w-full py-2 bg-[var(--bg-3)] hover:bg-[var(--border)] text-[var(--text-2)] rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dynamic Promotion Offer Popup */}
      <OfferPopup onSelectPlan={onPlanSelect} location="landing" />
    </div>
  );
}
